// ─────────────────────────────────────────────────────────────────────────────
// API Reference data
//
// Adding a new resource:
//   1. Add an entry to RESOURCES keyed by the URL slug.
//   2. Add it to SIDEBAR under the right group — the router automatically
//      renders /api-reference/<slug> from this data.
//
// Adding a new endpoint to an existing resource:
//   1. Append to the resource's `endpoints` array.
//
// The `envelope` / `errorEnvelope` helpers wrap example payloads in the
// standard response shape. Use them when building `responseExamples`.
// ─────────────────────────────────────────────────────────────────────────────

export const API_BASE_URL = 'https://api.valyx.com';

export const envelope = (message, data) => ({ error: null, message, data });
export const errorEnvelope = (error, message) => ({ error, message, data: null });

// ── Shared example objects (reused across endpoints) ─────────────────────────

const TAX_RATE = {
  id: 'txr_01abc',
  name: 'GST 18%',
  rate: 18.0,
  type: 'IGST',
  status: 'ACTIVE',
  createdBy: 'usr_01xyz',
};

const TAX_GROUP_EXPANDED = {
  id: 'txg_01abc',
  name: 'GST 18%',
  totalRate: 18.0,
  taxRates: [
    { taxRateId: 'txr_01abc', taxRateName: 'CGST 9%', rate: 9.0, type: 'CGST' },
    { taxRateId: 'txr_02def', taxRateName: 'SGST 9%', rate: 9.0, type: 'SGST' },
  ],
  status: 'ACTIVE',
  createdBy: 'usr_01xyz',
};

const TAX_GROUP_FLAT = {
  id: 'txg_01abc',
  name: 'GST 18%',
  totalRate: 18.0,
  taxRateIds: ['txr_01abc', 'txr_02def'],
  status: 'ACTIVE',
  createdBy: 'usr_01xyz',
};

const CUSTOMER = {
  id: 'cust_29_CUST-1042',
  buyerId: 'CUST-1042',
  name: 'Acme Industries Pvt Ltd',
  alias: 'Acme',
  countryCode: 'IN',
  country: 'India',
  stateCode: '29',
  currency: 'INR',
  registeredAddress: {
    line1: '221B Baker Street',
    line2: null,
    city: 'Bengaluru',
    state: 'Karnataka',
    stateCode: '29',
    country: 'India',
    zipCode: '560001',
    gstin: null,
  },
  taxIdentifiers: [
    { value: '29ABCDE1234F1Z5', type: 'GSTIN' },
  ],
  tags: { segment: 'enterprise' },
};

const CONTACT = {
  id: 'contact_abc123',
  customerId: 'cust_29_CUST-1042',
  name: 'Priya Sharma',
  role: 'AP',
  primaryPhone: '+919876543210',
  primaryEmail: 'priya@acme.example',
};

const CONTACT_2 = {
  id: 'contact_def456',
  customerId: 'cust_29_CUST-1042',
  name: 'Rahul Kumar',
  role: 'Finance Head',
  primaryPhone: null,
  primaryEmail: 'rahul@acme.example',
};

// ── Tax shape (reused in invoiceTax and lineItems[*].tax) ──────────────────

const TAX_INTRA_STATE_18 = {
  total: 18000.0,
  gstTax: {
    totalGst: 18000.0, totalGstRate: 18.0,
    cgst: 9000.0, cgstRate: 9.0,
    sgst: 9000.0, sgstRate: 9.0,
    igst: 0.0, igstRate: 0.0,
  },
  cessTax: null,
  serviceTax: null,
  reverseChargeTax: null,
  otherTaxes: [],
};

// AddressWithPhone — used in billingAddress, shippingAddress,
// sellerDetails.address, and buyerDetails.address. `pan` is response-only
// (auto-derived from gstin), so it's not in the request fields below.
const ADDRESS_WITH_PHONE_FIELDS = [
  { name: 'line1', type: 'string', required: false, description: 'First line of the street address.' },
  { name: 'line2', type: 'string', required: false, description: 'Second line (apartment, suite, building).' },
  { name: 'city', type: 'string', required: false, description: 'City or town.' },
  { name: 'state', type: 'string', required: false, description: 'State or province name.' },
  { name: 'stateCode', type: 'string', required: false, description: 'Two-digit GST state code.' },
  { name: 'country', type: 'string', required: false, description: 'Country name.' },
  { name: 'zipCode', type: 'string', required: false, description: 'Postal or ZIP code.' },
  { name: 'gstin', type: 'string', required: false, description: 'GSTIN for this address. When provided, the `pan` field on the response is auto-derived from it — you don\'t need to send `pan`.' },
  { name: 'primaryPhone', type: 'string', required: false, description: 'Primary phone number for this address.' },
];

const BANK_ACCOUNT_FIELDS = [
  { name: 'accountHolderName', type: 'string', required: false, description: 'Name on the bank account.' },
  { name: 'bankAccountNo', type: 'string', required: false, description: 'Bank account number.' },
  { name: 'ifscCode', type: 'string', required: false, description: 'IFSC code (for Indian bank transfers).' },
  { name: 'bankName', type: 'string', required: false, description: 'Name of the bank.' },
  { name: 'swiftCode', type: 'string', required: false, description: 'SWIFT / BIC code (for international wire transfers).' },
  { name: 'bankAccountId', type: 'string', required: false, description: 'Your own identifier for this bank account.' },
];

// Request-side Tax fields — only amounts are accepted. Rates (`*Rate`) are
// auto-calculated by Valyx and returned in the response, so they're not
// included here.
const TAX_FIELDS = [
  { name: 'total', type: 'number', required: false, description: 'Optional expected aggregate tax amount. Auto-computed from the sum of `gstTax.totalGst`, `cessTax.cess`, `serviceTax.serviceTax`, `reverseChargeTax.reverseChargeTax`, and every `otherTaxes[i].genericTax`. When supplied, Valyx returns a `400` if your value doesn\'t match the derived sum.' },
  {
    name: 'gstTax',
    type: 'GstTax | null',
    required: false,
    description: 'GST regime breakdown. Use `cgst` + `sgst` for **intra-state** supply, OR `igst` for **inter-state** — never both. CGST and SGST must be equal.',
    children: [
      { name: 'totalGst', type: 'number', required: false, description: 'Optional expected aggregate GST amount. Auto-computed from `cgst` + `sgst` (intra-state) or from `igst` (inter-state). When supplied, Valyx returns a `400` if your value doesn\'t match the derived sum.' },
      { name: 'cgst', type: 'number', required: false, description: 'Central GST amount (intra-state only). Must equal `sgst`.' },
      { name: 'sgst', type: 'number', required: false, description: 'State GST amount (intra-state only). Must equal `cgst`.' },
      { name: 'igst', type: 'number', required: false, description: 'Integrated GST amount (inter-state only).' },
    ],
  },
  {
    name: 'cessTax',
    type: 'CessTax | null',
    required: false,
    description: 'Cess (often paired with GST on luxury or sin goods).',
    children: [
      { name: 'cess', type: 'number', required: false, description: 'Cess amount.' },
    ],
  },
  {
    name: 'serviceTax',
    type: 'ServiceTax | null',
    required: false,
    description: 'Legacy pre-GST service tax. Most current callers should leave this `null` — kept on the model for invoice imports and archived records.',
    children: [
      { name: 'serviceTax', type: 'number', required: false, description: 'Service tax amount.' },
    ],
  },
  {
    name: 'reverseChargeTax',
    type: 'ReverseChargeTax | null',
    required: false,
    description: 'Reverse-charge tax — used when the **buyer** (not the seller) is liable to remit tax to the government, common in B2B services under GST. Does not stack with the GST split; treat it as a separate component.',
    children: [
      { name: 'reverseChargeTax', type: 'number', required: false, description: 'Reverse-charge tax amount.' },
    ],
  },
  {
    name: 'otherTaxes',
    type: 'array<OtherTax>',
    required: false,
    description: 'Catch-all for named taxes that don\'t fit the typed blocks (state-level surcharges, foreign-jurisdiction VAT, etc.). Defaults to `[]`.',
    children: [
      { name: 'name', type: 'string', required: true, description: 'Name of the tax (e.g. `VAT`, `Local levy`). Returned in the response unchanged.' },
      { name: 'genericTax', type: 'number', required: false, description: 'Tax amount.' },
    ],
  },
];

const INVOICE = {
  id: 'inv_29_INV-2026-0042',
  invoiceNumber: 'INV-2026-0042',
  customerId: 'cust_29_CUST-1042',
  invoiceDate: '2026-05-08',
  invoiceDueDate: '2026-06-07',
  promiseToPayDate: null,
  invoiceCurrency: 'INR',
  exchangeRate: 1.0,
  invoiceSubtotal: 100000.0,
  invoiceTax: TAX_INTRA_STATE_18,
  invoiceTotal: 118000.0,
  discount: null,
  placeOfSupply: 'Karnataka',
  invoiceType: null,
  invoiceDocumentType: 'INVOICE',
  lifecycleStatus: 'FINALIZED',
  invoiceStatus: 'DUE',
  paymentStatus: 'UNPAID',
  lineItems: [
    {
      name: 'Consulting hours',
      description: null,
      hsnOrSacCode: null,
      quantity: 100.0,
      unit: null,
      pricePerItem: 1000.0,
      subTotal: 100000.0,
      taxRate: 18.0,
      tax: TAX_INTRA_STATE_18,
      discount: null,
      total: 118000.0,
      lineItemType: null,
    },
  ],
  tags: {},
  salesDate: '2026-05-01',
  salesOrderNumber: 'SO-2026-0042',
  salesOrderId: null,
  generalTerms: null,
  paymentTerms: 'Net 30',
  externalUrl: null,
  billingAddress: {
    line1: '1 Bill Street',
    line2: null,
    city: 'Bengaluru',
    state: null,
    stateCode: '29',
    country: null,
    zipCode: null,
    gstin: null,
    pan: null,
    primaryPhone: '+919876543210',
  },
  shippingAddress: null,
  sellerDetails: {
    website: 'https://acme.example',
    address: {
      line1: 'Seller Plaza',
      line2: null,
      city: 'Bengaluru',
      state: null,
      stateCode: '29',
      country: null,
      zipCode: null,
      gstin: '29AABCU9603R1Z2',
      pan: 'AABCU9603R',
      primaryPhone: '+919999999999',
    },
    bankAccountDetails: {
      accountHolderName: 'Acme Industries',
      bankAccountNo: '0123456789',
      ifscCode: 'HDFC0000123',
      bankName: 'HDFC Bank',
      swiftCode: null,
      bankAccountId: null,
    },
  },
  buyerDetails: {
    address: {
      line1: 'Buyer Lane',
      line2: null,
      city: null,
      state: null,
      stateCode: '29',
      country: null,
      zipCode: null,
      gstin: null,
      pan: null,
      primaryPhone: null,
    },
    gstin: '29ABCDE1234F1Z5',
  },
};

const PROPERTY_DEFINITION = {
  id: 'def_abc123',
  propertyName: 'Product Line',
  propertyKey: 'product_line',
  documentType: 'INVOICE',
  dataType: 'STRING',
  isMandatory: false,
  defaultValue: null,
};

const PROPERTY_VALUES_RESPONSE = [
  {
    propertyKey: 'product_line',
    propertyName: 'Product Line',
    value: 'North',
  },
  {
    propertyKey: 'priority_score',
    propertyName: 'Priority Score',
    value: null,
  },
];

// ── Shared shapes for the /billing/* namespaces ──────────────────────────────
//
// The paginated `filter` endpoints and the by-field `updates` envelope repeat
// across fees, products, usage attributes, and rate attributes. These builders
// keep the wording identical without duplicating the rows. `opExample` differs
// per namespace because the source specs differ — don't normalize it.

const paginationFields = (opExample) => [
  { name: 'pageNumber', type: 'integer', required: true, description: '1-based page index.' },
  { name: 'pageSize', type: 'integer', required: true, description: 'Number of records per page.' },
  { name: 'orderBy', type: 'string', required: false, description: 'Field name to sort by.' },
  {
    name: 'filterDicts',
    type: 'array<FilterDict>',
    required: false,
    description: 'Filtering conditions. Each entry specifies a single field-level filter. The exact shape depends on the filter operator — the fields below describe the common pattern.',
    children: [
      { name: 'field', type: 'string', required: false, description: 'Name of the field to filter on, e.g. `name`.' },
      { name: 'op', type: 'string', required: false, description: `Filter operator, e.g. \`${opExample}\`.` },
      { name: 'value', type: 'any', required: false, description: 'Value to compare against.' },
    ],
  },
];

const pageResponseFields = (itemType, itemDescription) => [
  { name: 'data.totalDocs', type: 'integer', description: 'Total number of records matching the filter.' },
  { name: 'data.docs', type: `array<${itemType}>`, description: itemDescription },
  { name: 'data.hasNext', type: 'boolean', description: 'Whether a page exists after this one.' },
  { name: 'data.hasPrev', type: 'boolean', description: 'Whether a page exists before this one.' },
  { name: 'data.pageNumber', type: 'integer', description: 'The page number that was returned.' },
  { name: 'data.pageSize', type: 'integer', description: 'Number of records per page.' },
  { name: 'data.totalPages', type: 'integer', description: 'Total number of pages at the requested `pageSize`.' },
  { name: 'data.prevPageNumber', type: 'integer | null', description: 'Previous page number, or `null` on the first page.' },
  { name: 'data.nextPageNumber', type: 'integer | null', description: 'Next page number, or `null` on the last page.' },
];

// Errors shared by every paginated `filter` endpoint. `pageNumber` / `pageSize`
// are validated by CommonUtils.validate_pagination_params on the way through,
// so omitting them is a 400 even where the request model types them optional.
const PAGINATION_ERRORS = [
  { status: 400, when: '`pageNumber` is missing or not a positive integer.' },
  { status: 400, when: '`pageSize` is missing or not a positive integer.' },
  { status: 403, when: 'Missing, invalid, or expired API key.' },
];

// The by-field update envelope. `valueFields` are the rows allowed under
// `updates.values` for that particular resource.
const updatesBody = (valueFields) => [
  {
    name: 'updates',
    type: 'object',
    required: true,
    description: 'The set of changes to apply.',
    children: [
      { name: 'fields', type: 'string[]', required: true, description: 'Names of the fields to update. Only these keys are read from `values`.' },
      { name: 'values', type: 'object', required: true, description: 'New values, keyed by field name.', children: valueFields },
    ],
  },
];

// `updates.fields` and `updates.values` must agree: the server walks the parsed
// request object and rejects any field that carries a non-null value while being
// absent from `updates.fields`. Extra values are NOT silently ignored.
const UPDATES_DESCRIPTION =
  '`updates.fields` and `updates.values` must agree. Every field you send in `updates.values` with a non-null value **must** also be listed in `updates.fields` — if it isn\'t, the whole request fails with a `400` and nothing is changed. The reverse is fine: naming a field in `updates.fields` without supplying it in `updates.values` is accepted.';

const UPDATES_MISMATCH_ERROR = {
  status: 400,
  when: 'A field in `updates.values` holds a non-null value but is not listed in `updates.fields` (`Field \'x\' is not in fields_to_update but has a non-None value`).',
};

const pageExample = (docs) => ({
  totalDocs: docs.length,
  docs,
  hasNext: false,
  hasPrev: false,
  pageNumber: 1,
  pageSize: 20,
  totalPages: 1,
  prevPageNumber: null,
  nextPageNumber: null,
});

// ── Fee shapes (reused across create and update) ─────────────────────────────

const USAGE_ATTRIBUTE_FIELDS = [
  { name: 'apiCode', type: 'string', required: true, description: 'The `apiName` of a usage attribute defined on the **same product** (see [Usage Attributes](api-reference/usage-attributes)). Validated against that product\'s usage attributes — an unknown code is a `400`.' },
  { name: 'weight', type: 'number', required: true, description: 'Contribution weight for quantity calculation. Must be a positive number — zero and negatives are rejected.' },
];

const CURRENCY_TAX_FIELDS = [
  { name: 'currency', type: 'string', required: true, description: 'ISO currency code. A currency may appear at most once across the whole array — duplicates are a `400`.' },
  { name: 'taxRateId', type: 'string | null', required: false, description: 'Tax rate ID to apply for this currency. Mutually exclusive with `taxGroupId` — sending both is a `400`.' },
  { name: 'taxGroupId', type: 'string | null', required: false, description: 'Tax group ID to apply for this currency. Mutually exclusive with `taxRateId` — sending both is a `400`.' },
];

const BILL_LIMIT_TIER_FIELDS = [
  { name: 'minValue', type: 'number | null', required: false, description: 'Minimum bill amount for this tier. At least one of `minValue` / `maxValue` must be set — a tier with both `null` is a `400`. When both are set, `minValue` must not exceed `maxValue`.' },
  { name: 'maxValue', type: 'number | null', required: false, description: 'Maximum bill amount for this tier. At least one of `minValue` / `maxValue` must be set.' },
  { name: 'upTo', type: 'number', required: false, description: 'Upper usage boundary of the tier — the tier applies to usage up to this value. Defaults to infinity when omitted or `null`.' },
];

// The BillLimit shape is shared verbatim between per-currency fee limits and
// the contract-level `billingReportLimit` / `contractLimit`.
const BILL_LIMIT_FIELDS = [
  { name: 'isUsageBased', type: 'boolean', required: false, description: 'Whether the limit varies by usage. Defaults to `false`. When `true`, a non-empty `tiers` array is required.' },
  { name: 'minValue', type: 'number | null', required: false, description: 'Minimum bill amount (floor).' },
  { name: 'maxValue', type: 'number | null', required: false, description: 'Maximum bill amount (cap).' },
  { name: 'isProrated', type: 'boolean | null', required: false, description: 'Whether the limit is prorated for partial periods.' },
  { name: 'isActive', type: 'boolean | null', required: false, description: 'Whether the limit is in force. Defaults to `true`.' },
  {
    name: 'tiers',
    type: 'array<BillLimitTier>',
    required: false,
    description: 'Usage tiers for the limit. Required and must be non-empty when `isUsageBased` is `true`; ignored otherwise.',
    children: BILL_LIMIT_TIER_FIELDS,
  },
];

const CURRENCY_BILL_LIMIT_FIELDS = [
  { name: 'currency', type: 'string', required: true, description: 'ISO currency code.' },
  {
    name: 'billLimit',
    type: 'BillLimit',
    required: true,
    description: 'Bill limit configuration for this currency.',
    children: BILL_LIMIT_FIELDS,
  },
];

const FEE = {
  id: 'fee_abc123',
  name: 'Platform Fee',
  productId: 'prod_abc123',
  type: 'FIXED',
  hsnSac: '998314',
  currencyWiseTax: [],
  currencyWiseBillLimit: [],
  currencies: ['INR', 'USD'],
  usageAttributes: [],
  rateAttributes: [],
  rateCards: [],
  unit: null,
  productName: 'Logistics Service',
  rateConfiguration: 'MULTIPLE',
  description: 'Monthly platform access charge',
  createdAt: '2026-06-02T10:00:00',
  updatedAt: '2026-06-02T10:00:00',
  updatedBy: null,
};

const FEE_LIST_PAGE = pageExample([
  {
    id: 'fee_abc123',
    name: 'Platform Fee',
    productId: 'prod_abc123',
    type: 'METERED',
    hsnSac: null,
    currencyWiseTax: [],
    currencyWiseBillLimit: [],
    currencies: ['INR'],
    usageAttributes: [
      { apiCode: 'shipment_weight', weight: 1.0, name: 'Shipment Weight' },
    ],
    rateAttributes: [
      { apiCode: 'origin_region', name: 'Origin Region', groups: [['North', 'South'], ['East', 'West']] },
    ],
    rateCards: [],
    unit: null,
    productName: 'Logistics Service',
    rateConfiguration: 'MULTIPLE',
    description: null,
    createdAt: '2026-06-02T10:00:00',
    updatedAt: '2026-06-02T10:00:00',
    updatedBy: null,
  },
]);

// ── Contract shapes ──────────────────────────────────────────────────────────

const TIME_INTERVAL_FIELDS = [
  { name: 'unit', type: 'enum', enumRef: 'TimeUnit', required: true, description: 'One of `DAY`, `WEEK`, `MONTH`, `YEAR`.' },
  { name: 'value', type: 'integer', required: true, description: 'How many units. Must be an integer greater than 0.' },
];

const CONTRACT_ADDRESS_FIELDS = [
  { name: 'line1', type: 'string', required: false, description: 'First line of the street address.' },
  { name: 'line2', type: 'string', required: false, description: 'Second line (apartment, suite, building).' },
  { name: 'state', type: 'string', required: false, description: 'State or province name. Used to validate `gstin` — the GSTIN\'s first two digits must match this state\'s GST code.' },
  { name: 'city', type: 'string', required: false, description: 'City or town.' },
  { name: 'country', type: 'string', required: false, description: 'Country name.' },
  { name: 'zipCode', type: 'string', required: false, description: 'Postal or ZIP code.' },
  { name: 'gstin', type: 'string', required: false, description: 'GSTIN for this address.' },
  { name: 'pan', type: 'string', required: false, description: 'PAN for this address.' },
  { name: 'stateCode', type: 'string', required: false, description: 'Two-digit GST state code.' },
  { name: 'isDefault', type: 'boolean', required: false, description: 'Whether this is the default address. Defaults to `false`.' },
];

const DISCOUNT_TIER_FIELDS = [
  { name: 'amount', type: 'number', required: false, description: 'Flat discount for this tier. Defaults to `0.0`. Cannot be negative, and cannot be combined with a positive `percentage`.' },
  { name: 'percentage', type: 'number', required: false, description: 'Percentage discount for this tier. Defaults to `0.0`. Cannot be negative, and cannot be combined with a positive `amount`.' },
  { name: 'isPostTax', type: 'boolean', required: false, description: 'Whether the discount applies after tax. Defaults to `false`.' },
  { name: 'upTo', type: 'number', required: false, description: 'Upper usage bound for this tier. Defaults to infinity.' },
];

const BILLING_DISCOUNT_FIELDS = [
  { name: 'amount', type: 'number', required: false, description: 'Flat discount amount. Defaults to `0.0`.' },
  { name: 'percentage', type: 'number', required: false, description: 'Percentage discount. Defaults to `0.0`. Must be between 0 and 100.' },
  { name: 'isPostTax', type: 'boolean', required: false, description: 'Whether the discount applies after tax. Defaults to `false`.' },
  { name: 'amountBcy', type: 'number | null', required: false, description: 'Discount amount converted to your base currency. Derived server-side — don\'t send it.' },
  { name: 'discountType', type: 'enum | null', enumRef: 'BillingDiscountType', required: false, description: 'Currently only `ADJUSTMENT`.' },
  { name: 'discountName', type: 'string | null', required: false, description: 'Display name for the discount.' },
  { name: 'isUsageBased', type: 'boolean', required: false, description: 'Whether the discount varies by usage. Defaults to `false`. When `true`, populate `tiers`.' },
  { name: 'tiers', type: 'array<DiscountTier> | null', required: false, description: 'Usage tiers. Only meaningful when `isUsageBased` is `true`.', children: DISCOUNT_TIER_FIELDS },
  { name: 'priority', type: 'integer', required: false, description: 'Application order — lower value applies first. Defaults to `1`. Must be greater than 0.' },
  { name: 'isStackable', type: 'boolean', required: false, description: 'Whether billing keeps applying later discounts after this one. Defaults to `false` — a non-stackable discount is applied alone.' },
  {
    name: 'duration',
    type: 'BillingInterval | null',
    required: false,
    description: 'Billing-period-index window the discount is active for. `null` means it applies indefinitely. **Contract-level discounts must leave this unset** — a non-null value is rejected there.',
    children: [
      { name: 'startIndex', type: 'integer', required: true, description: 'First billing period the discount applies to. 1-indexed.' },
      { name: 'endIndex', type: 'integer', required: true, description: 'Last billing period the discount applies to.' },
    ],
  },
  {
    name: 'period',
    type: 'BillingDiscountPeriod | null',
    required: false,
    description: 'Date-based window the discount is active for — the newer alternative to `duration`.',
    children: [
      { name: 'startDate', type: 'string | null', required: false, description: 'Start of the window, `YYYY-MM-DD`.' },
      { name: 'endDate', type: 'string | null', required: false, description: 'End of the window, `YYYY-MM-DD`. Must not be earlier than `startDate`.' },
    ],
  },
];

const FEE_COMPONENT_TAX_FIELDS = [
  { name: 'name', type: 'string', required: true, description: 'Tax name, e.g. `IGST`, `CGST`, `SGST`.' },
  { name: 'value', type: 'number', required: true, description: 'Tax rate as a percentage. Must be greater than 0.' },
  { name: 'isActive', type: 'boolean', required: false, description: 'Whether the tax is currently applied. Defaults to `true`.' },
  { name: 'taxCategory', type: 'enum | null', enumRef: 'TaxCategory', required: false, description: 'Auto-derived from the uppercased `name` when omitted, falling back to `OTHER` if it matches no known category.' },
];

const FEE_COMPONENT_PERIOD_FIELDS = [
  { name: 'startDate', type: 'string | null', required: false, description: 'Start of the window the fee is active in, `YYYY-MM-DD`.' },
  { name: 'endDate', type: 'string | null', required: false, description: 'End of the window the fee is active in, `YYYY-MM-DD`.' },
  { name: 'startPeriodIndex', type: 'integer | null', required: false, description: 'Alternative to `startDate` — a 1-indexed billing period number.' },
  { name: 'endPeriodIndex', type: 'integer | null', required: false, description: 'Alternative to `endDate` — a 1-indexed billing period number.' },
];

const ADVANCE_CONFIG_FIELDS = [
  { name: 'trueUpEnabled', type: 'boolean', required: false, description: 'Enables true-up adjustments when actual usage exceeds the estimate. Defaults to `false`.' },
  { name: 'trueDownEnabled', type: 'boolean', required: false, description: 'Enables true-down adjustments when actual usage falls short of the estimate. Defaults to `false`.' },
  { name: 'actualUsageOverride', type: 'boolean', required: false, description: 'Bill on actual usage instead of the estimate. Defaults to `false`.' },
  { name: 'isUsageEstimateProrated', type: 'boolean', required: false, description: 'Whether uploaded usage estimates are prorated. Defaults to `true`.' },
  { name: 'fileName', type: 'string | null', required: false, description: 'Name of the uploaded estimate file.' },
  { name: 'attachmentId', type: 'string | null', required: false, description: 'Storage reference for the uploaded estimate file.' },
  { name: 'useForOverageCalculation', type: 'boolean', required: false, description: 'Calculate overage against the contract-level minimum commitment. Defaults to `false`. **Set it consistently across every fee component on the contract** — if even one is `false`, the system falls back to per-component estimate-based overage.' },
];

// Shared by `additionalParams` on attach and `updates.values` on the
// fee-component update endpoint — they are the same underlying model.
const CONTRACT_FEE_COMPONENT_FIELDS = [
  { name: 'billingFrequency', type: 'TimeInterval | null', required: false, description: 'How often this fee is billed. **Required when attaching** — attach fails with `billing_frequency must be provided when adding a fee component` if it is absent.', children: TIME_INTERVAL_FIELDS },
  { name: 'activePeriod', type: 'FeeComponentPeriod | null', required: false, description: 'Window during which the fee is active, by date or by billing-period index.', children: FEE_COMPONENT_PERIOD_FIELDS },
  { name: 'taxRate', type: 'array<FeeComponentTax>', required: false, description: 'Taxes applied to this fee component. Defaults to `[]`.', children: FEE_COMPONENT_TAX_FIELDS },
  { name: 'billLimit', type: 'BillLimit | null', required: false, description: 'Cap and floor for this fee component.', children: BILL_LIMIT_FIELDS },
  { name: 'discounts', type: 'array<BillingDiscount>', required: false, description: 'Discounts on this fee component\'s relationship with the contract. Defaults to `[]`. Unlike contract-level discounts, these **may** set `duration` or `period`.', children: BILLING_DISCOUNT_FIELDS },
  { name: 'includedInUsage', type: 'boolean', required: false, description: 'Whether this fee\'s usage feeds contract-level usage totals (min/max and discount tiers). Defaults to `false`.' },
  { name: 'usageCycle', type: 'TimeInterval | null', required: false, description: 'How often usage is aggregated for this fee.', children: TIME_INTERVAL_FIELDS },
  { name: 'estimateCycle', type: 'TimeInterval | null', required: false, description: 'How often usage estimates are generated.', children: TIME_INTERVAL_FIELDS },
  { name: 'virtualStartDate', type: 'string | null', required: false, description: 'Anchor date used to compute billing periods, `YYYY-MM-DD`.' },
  { name: 'nextBillingDate', type: 'string | null', required: false, description: 'Overrides the next computed billing date, `YYYY-MM-DD`.' },
  { name: 'isPreusage', type: 'boolean', required: false, description: 'Marks the fee as prepaid / estimate-based. Defaults to `false`. Cannot be changed on an `ACTIVE` contract.' },
  { name: 'advanceConfig', type: 'AdvanceConfig | null', required: false, description: 'True-up / true-down configuration for prepaid billing.', children: ADVANCE_CONFIG_FIELDS },
  { name: 'usageDriverWeights', type: 'array<UsageDriverWeight>', required: false, description: 'Per-usage-driver weights, each with a non-overlapping list of weighted periods. Defaults to `[]`.' },
  { name: 'firstBillingPeriodEndDate', type: 'string | null', required: false, description: 'Overrides the end date of the first billing period, `YYYY-MM-DD`. Otherwise derived as `virtualStartDate + billingFrequency − 1 day`.' },
  { name: 'estimateUpsertRequests', type: 'array<UsageEstimate>', required: false, description: 'Usage-estimate rows to upsert alongside this change. Defaults to `[]`.' },
];

const CONTRACT = {
  id: 'contract_abc123',
  autoRenew: false,
  customerId: 'cust_29_CUST-1042',
  contractNumber: 'CONTRACT-001',
  contractTitle: 'Annual Logistics Agreement',
  contractPeriod: { unit: 'YEAR', value: 1 },
  noteContract: null,
  startDate: '2026-01-01',
  endDate: '2026-12-31',
  nextBillingDate: '2026-02-01',
  billingDiscounts: [],
  creditTermId: 'term_net30',
  poNumber: 'PO-9876',
  noteInvoice: null,
  terms: null,
  companyId: 'comp_01xyz',
  createdBy: 'usr_01xyz',
  updatedBy: 'usr_01xyz',
  status: 'DRAFT',
  customerName: 'Acme Industries Pvt Ltd',
  billingReportLimit: null,
  contractLimit: null,
  buyerId: null,
  billingAddress: {
    line1: '221B Baker Street',
    line2: null,
    state: 'Karnataka',
    city: 'Bengaluru',
    country: 'India',
    zipCode: '560001',
    gstin: '29ABCDE1234F1Z5',
    pan: 'ABCDE1234F',
    stateCode: '29',
    isDefault: false,
  },
  shippingAddress: null,
  gstin: '29ABCDE1234F1Z5',
  companyGstin: null,
  placeOfSupply: null,
  currency: 'INR',
  splitConfigMeta: null,
  isSplitConfigEnabled: false,
  gstTreatment: null,
  comments: [],
  customFields: null,
};

const CONTRACT_LIST_PAGE = pageExample([
  { ...CONTRACT, status: 'ACTIVE', customFields: { region: 'North' } },
]);

const CONTRACT_FEE_COMPONENT = {
  id: 'cfc_rel_abc123',
  isPreusage: false,
  productId: 'prod_abc123',
  productName: 'Logistics Service',
  sku: 'SKU-001',
  includedInUsage: true,
  name: 'Platform Fee',
  description: 'Monthly platform access charge',
  hsnOrSacCode: '998314',
  usage: 1500.0,
  billedAmount: 3000.0,
  estimatedUsage: null,
  estimatedBilledAmount: null,
  adjustment: null,
  taxRate: [{ name: 'IGST', value: 18.0, isActive: true, taxCategory: 'GST' }],
  billLimit: null,
  activePeriod: { startDate: '2026-01-01', endDate: null, startPeriodIndex: null, endPeriodIndex: null },
  billingFrequency: { unit: 'MONTH', value: 1 },
  usageCycle: { unit: 'MONTH', value: 1 },
  virtualStartDate: '2026-01-01',
  unit: 'kg',
  isFlatFee: false,
  companyId: 'comp_01xyz',
  createdBy: 'usr_01xyz',
  updatedBy: 'usr_01xyz',
  ratecards: [],
  lastRefreshed: '2026-02-01T10:30:00',
  estimates: null,
  advanceConfig: null,
  firstBillingPeriodEndDate: '2026-01-31',
  estimateCycle: null,
  discounts: [],
  estimateCycleWiseSummary: null,
  applicableBillingPeriod: { startDate: '2026-01-01', endDate: '2026-01-31' },
};

const CONTRACT_RATECARD_RELATION = {
  id: 'cfcr_001',
  contractId: 'contract_abc123',
  feeComponentId: 'fee_abc123',
  ratecardId: 'rc_abc123',
  tags: { origin_region: 'North-South' },
  companyId: 'comp_01xyz',
  createdBy: 'usr_01xyz',
};

// ── Rate card shapes ─────────────────────────────────────────────────────────
//
// `description` is polymorphic: the server merges `billingScheme` into it and
// dispatches to a pricing-strategy class, so which children are valid depends
// on the scheme. Documented as one table with per-scheme applicability notes.

const PRICE_TIER_FIELDS = [
  { name: 'upTo', type: 'number | "inf"', required: true, description: 'Upper usage boundary of this tier. Must be greater than 0 and a whole number; pass the string `"inf"` for an unbounded final tier. Tiers are sorted by `upTo`, and two tiers may not share the same value.' },
  { name: 'unitPrice', type: 'number', required: false, description: 'Price per unit within this tier. Defaults to `0`. May not be `null`.' },
  { name: 'flatPrice', type: 'number', required: false, description: 'Flat amount added whenever this tier applies, regardless of quantity. Defaults to `0`. May not be `null`.' },
  { name: 'name', type: 'string', required: false, description: 'Label for the tier, echoed back in validation messages.' },
  { name: 'isPercentage', type: 'boolean', required: false, description: 'Treat `unitPrice` as a percentage of the usage amount rather than a rate. Defaults to `false`.' },
  { name: 'repeatTier', type: 'boolean', required: false, description: 'When `true`, this tier repeats at the same interval to infinity and every tier after it is ignored. Defaults to `false`.' },
];

const PRICING_DESCRIPTION_FIELDS = [
  { name: 'isProrated', type: 'boolean', required: false, description: 'Whether the price is prorated. Defaults to `false`. Applies to every scheme.' },
  {
    name: 'tiers',
    type: 'array<PriceTier>',
    required: false,
    description: '`VOLUME` and `GRADUATED` **only** — required for those two, and rejected for every other scheme. The tier ladder. Must be non-empty.',
    children: PRICE_TIER_FIELDS,
  },
  { name: 'perUnitPrice', type: 'number', required: false, description: '`PER_UNIT` **only — required.** Price charged for every unit.' },
  { name: 'isPercentage', type: 'boolean', required: false, description: '`PER_UNIT` and `MINMAX` **only.** Treats the per-unit value as a percentage of the usage amount. Required for `MINMAX`; optional (default `false`) for `PER_UNIT`.' },
  { name: 'fixedPrice', type: 'number', required: false, description: '`FIXED_RATE` **only — required.** Constant price, independent of usage.' },
  { name: 'operationType', type: 'enum', enumRef: 'MinMaxOperation', required: false, description: '`MINMAX` **only — required.** `MINIMUM` charges the lower of the usage-based price and `flatRate`; `MAXIMUM` charges the higher.' },
  { name: 'perUsageValue', type: 'number', required: false, description: '`MINMAX` **only — required.** Rate applied to usage — a per-unit price, or a percentage of the usage amount when `isPercentage` is `true`.' },
  { name: 'flatRate', type: 'number', required: false, description: '`MINMAX` **only.** Flat amount compared against the usage-based price. At least one of `flatRate` / `perCountValue` must be supplied.' },
  { name: 'perCountValue', type: 'number', required: false, description: '`MINMAX` **only.** Rate applied per usage count. At least one of `flatRate` / `perCountValue` must be supplied.' },
  { name: 'isPercentageForCount', type: 'boolean', required: false, description: '`MINMAX` **only.** Whether `perCountValue` is a percentage. Required whenever `perCountValue` is supplied.' },
];

const GRADUATED_DESCRIPTION = {
  billingScheme: 'GRADUATED',
  isProrated: false,
  tiers: [
    { upTo: 100, unitPrice: 15, flatPrice: 0, name: 'First 100 kg', isPercentage: false, repeatTier: false },
    { upTo: 500, unitPrice: 10, flatPrice: 0, name: 'Next 400 kg', isPercentage: false, repeatTier: false },
    { upTo: 'inf', unitPrice: 8, flatPrice: 0, name: 'Beyond 500 kg', isPercentage: false, repeatTier: false },
  ],
};

const FEE_RATECARD = {
  id: 'rc_abc123',
  name: 'Standard Freight Rates',
  feeComponentId: 'fee_abc123',
  companyId: 'comp_01xyz',
  currency: 'INR',
  isDefault: false,
  description: GRADUATED_DESCRIPTION,
  ratecardDetail: 'Upto 100 kg - ₹ 15/kg\nUpto 500 kg - ₹ 10/kg\nUpto Infinity kg - ₹ 8/kg',
  ratecardDescription: 'Volume freight pricing for northern routes',
  currencyId: null,
  createdBy: 'usr_01xyz',
  contractId: null,
};

const FEE_RATECARD_GROUP_PAGE = pageExample([
  {
    attributes: { origin_region: ['North', 'South'] },
    ratecards: [FEE_RATECARD],
    defaultRatecard: null,
  },
]);

// ── Product shapes ───────────────────────────────────────────────────────────

const PRODUCT = {
  id: 'prod_abc123',
  name: 'Logistics Service',
  code: 'LOGI-001',
  description: 'Freight and logistics billing product',
  hsnSac: null,
  usageBasedBilling: true,
  rateConfiguration: 'MULTIPLE',
  usageAttributeCount: 0,
  rateAttributeCount: 0,
  feeCount: 0,
  lastModifiedBy: null,
  lastUpdatedAt: null,
};

const PRODUCT_ESTABLISHED = {
  id: 'prod_abc123',
  name: 'Logistics Service',
  code: 'LOGI-001',
  description: null,
  hsnSac: null,
  usageBasedBilling: true,
  rateConfiguration: 'MULTIPLE',
  usageAttributeCount: 2,
  rateAttributeCount: 1,
  feeCount: 3,
  lastModifiedBy: 'user_xyz',
  lastUpdatedAt: '2026-06-01T09:30:00',
};

const PRODUCT_LIST_PAGE = pageExample([PRODUCT_ESTABLISHED]);

const USAGE_ATTRIBUTE = {
  id: 'ua_abc123',
  productId: 'prod_xyz',
  name: 'Shipment Weight',
  apiName: 'shipment_weight',
  unit: 'kg',
  description: 'Total weight of the shipment in kilograms',
  createdAt: '2026-06-02T10:00:00',
};

const USAGE_ATTRIBUTE_LIST_PAGE = pageExample([
  {
    id: 'ua_abc123',
    productId: 'prod_xyz',
    name: 'Shipment Weight',
    apiName: 'shipment_weight',
    unit: 'kg',
    description: null,
    createdAt: '2026-06-01T09:00:00',
  },
]);

const RATE_ATTRIBUTE = {
  id: 'ra_abc123',
  productId: 'prod_xyz',
  name: 'Origin Region',
  apiName: 'origin_region',
  groups: [['North', 'South'], ['East', 'West']],
  description: 'Identifies the freight origin zone',
  createdAt: '2026-06-02T10:00:00',
};

const RATE_ATTRIBUTE_LISTED = {
  id: 'ra_abc123',
  productId: 'prod_xyz',
  name: 'Origin Region',
  apiName: 'origin_region',
  groups: [['North', 'South'], ['East', 'West']],
  description: null,
  createdAt: '2026-06-01T09:00:00',
};

const RATE_ATTRIBUTE_LIST_PAGE = pageExample([RATE_ATTRIBUTE_LISTED]);

// ── Resources ────────────────────────────────────────────────────────────────

export const RESOURCES = {
  'tax-rates': {
    id: 'tax-rates',
    label: 'Tax Rates',
    tagline: 'Individual tax percentages',
    description: 'Tax rates define a percentage value and a tax type (IGST, CGST, SGST, UTGST, or CESS). Use them individually on line items, or combine them into tax groups for compound rates like split GST.',
    object: {
      name: 'The tax rate object',
      fields: [
        { name: 'id', type: 'string', description: 'Unique tax rate identifier.' },
        { name: 'name', type: 'string', description: 'Human-readable label, e.g. "GST 18%".' },
        { name: 'rate', type: 'float', description: 'Tax percentage. 18.0 represents 18%.' },
        { name: 'type', type: 'enum', enumRef: 'TaxType', description: 'One of IGST, CGST, SGST, UTGST, CESS.' },
        { name: 'status', type: 'enum', enumRef: 'Status', description: 'Only ACTIVE rates appear in list responses.' },
        { name: 'createdBy', type: 'string', description: 'User ID of the creator.' },
      ],
      example: TAX_RATE,
    },
    endpoints: [
      {
        id: 'list-tax-rates',
        method: 'GET',
        path: '/app/tax/list',
        title: 'List all tax rates',
        description: 'Returns every **active** tax rate for the authenticated company. Archived (`INACTIVE`) rates are excluded from this response.',
        responseFields: [
          { name: 'data', type: 'array<TaxRate>', description: 'All active tax rates for the company.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Tax rates fetched successfully', [TAX_RATE]) },
        ],
        errors: [
          { status: 403, when: 'Missing or invalid credentials.' },
        ],
      },
      {
        id: 'create-tax-rate',
        method: 'POST',
        path: '/app/tax/create',
        title: 'Create a tax rate',
        description: 'Creates a new tax rate with `status = ACTIVE`. `companyId` and `createdBy` are derived from the auth context — do not send them in the request body.',
        requestBody: [
          { name: 'name', type: 'string', required: true, description: 'Non-empty label (whitespace is trimmed).' },
          { name: 'rate', type: 'float', required: true, description: 'Non-negative tax percentage.' },
          { name: 'type', type: 'enum', required: true, enumRef: 'TaxType', description: 'One of IGST, CGST, SGST, UTGST, CESS.' },
        ],
        requestExample: { name: 'GST 18%', rate: 18.0, type: 'IGST' },
        responseExamples: [
          { label: '201 Created', status: 201, body: envelope('Tax created successfully', TAX_RATE) },
          { label: '400 Invalid', status: 400, body: errorEnvelope('ValidationError', 'Tax rate cannot be negative') },
        ],
        errors: [
          { status: 400, when: '`name` is missing or blank.' },
          { status: 400, when: '`rate` is missing, not a number, or negative.' },
          { status: 400, when: '`type` is missing or not a valid enum value.' },
          { status: 403, when: 'Missing or invalid credentials.' },
        ],
      },
    ],
  },

  'tax-groups': {
    id: 'tax-groups',
    label: 'Tax Groups',
    tagline: 'Bundles of tax rates',
    description: 'A tax group combines one or more tax rates into a single applicable rate. The `totalRate` is computed server-side as the sum of the constituent rates.',
    object: {
      name: 'The tax group object',
      fields: [
        { name: 'id', type: 'string', description: 'Unique tax group identifier.' },
        { name: 'name', type: 'string', description: 'Human-readable label.' },
        { name: 'totalRate', type: 'float', description: 'Sum of all constituent rates, computed server-side.' },
        { name: 'taxRates', type: 'array<TaxRateRef>', description: 'Constituent tax rates, expanded inline. Only returned by the list endpoint.' },
        { name: 'taxRateIds', type: 'array<string>', description: 'IDs of constituent rates. Returned by the create endpoint.' },
        { name: 'status', type: 'enum', enumRef: 'Status', description: 'Only ACTIVE groups appear in list responses.' },
        { name: 'createdBy', type: 'string', description: 'User ID of the creator.' },
      ],
      example: TAX_GROUP_EXPANDED,
    },
    endpoints: [
      {
        id: 'list-tax-groups',
        method: 'GET',
        path: '/app/tax/group/list',
        title: 'List all tax groups',
        description: 'Returns every **active** tax group with its constituent rates expanded inline.',
        responseFields: [
          { name: 'data', type: 'array<TaxGroup>', description: 'Tax groups with `taxRates` expanded to full objects.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Tax groups fetched successfully', [TAX_GROUP_EXPANDED]) },
        ],
        errors: [
          { status: 403, when: 'Missing or invalid credentials.' },
        ],
      },
      {
        id: 'create-tax-group',
        method: 'POST',
        path: '/app/tax/group/create',
        title: 'Create a tax group',
        description: 'Creates a new tax group from existing tax rate IDs. `totalRate` is computed server-side as the sum of the referenced rates.\n\nThe response returns `taxRateIds` (flat IDs) rather than the expanded `taxRates` — use the list endpoint for the expanded form.',
        requestBody: [
          { name: 'name', type: 'string', required: true, description: 'Non-empty label (whitespace is trimmed).' },
          { name: 'taxRateIds', type: 'array<string>', required: true, description: 'Non-empty array of existing tax rate IDs.' },
        ],
        requestExample: { name: 'GST 18%', taxRateIds: ['txr_01abc', 'txr_02def'] },
        responseExamples: [
          { label: '201 Created', status: 201, body: envelope('Tax group created successfully', TAX_GROUP_FLAT) },
        ],
        errors: [
          { status: 400, when: '`name` is missing or blank.' },
          { status: 400, when: '`taxRateIds` is missing, empty, or not an array.' },
          { status: 400, when: 'Any entry in `taxRateIds` is empty or not a string.' },
          { status: 403, when: 'Missing or invalid credentials.' },
        ],
      },
    ],
  },

  customers: {
    id: 'customers',
    label: 'Customers',
    tagline: 'The legal entities you bill',
    description: 'A customer represents an entity that will be billed.',
    object: {
      name: 'The customer object',
      fields: [
        { name: 'id', type: 'string', description: 'Auto-generated ID for the customer. Created by Valyx and returned in the response — you don\'t need to send this.' },
        { name: 'buyerId', type: 'string', description: 'Your own unique identifier for the customer — typically a CRM ID, ERP code, or similar. Must be unique within your account; you can\'t create two customers with the same `buyerId`.' },
        { name: 'name', type: 'string', description: 'Legal name of the customer. Once set, it can\'t be changed.' },
        { name: 'alias', type: 'string', description: 'Alternate identifier or display name for the customer. Can be changed later. Auto-populated from `name` if you don\'t send one.' },
        { name: 'countryCode', type: 'string', description: 'Two-letter country code for the customer\'s country (e.g. `IN` for India, `US` for the United States, `GB` for the United Kingdom). Defaults to `IN`.' },
        { name: 'country', type: 'string', description: 'Country name. Auto-populated from `countryCode` — you don\'t need to send this.' },
        { name: 'stateCode', type: 'string | null', description: 'Two-digit GST state code identifying the customer\'s state under India\'s GST system (e.g. `29` for Karnataka, `27` for Maharashtra, `07` for Delhi). Matches the first two digits of the customer\'s GSTIN. Required for Indian customers — it\'s used to determine the correct GST treatment (CGST + SGST for intra-state, IGST for inter-state) on their invoices.' },
        { name: 'currency', type: 'string', description: 'Three-letter ISO currency code (e.g. `INR`, `USD`, `EUR`). All invoices, credit notes, and other billing documents for this customer are issued in this currency. Defaults to `INR`.' },
        { name: 'registeredAddress', type: 'Address | null', description: 'Registered or billing address. See [Addresses](api-reference/addresses) for the field structure.' },
        { name: 'taxIdentifiers', type: 'array<TaxIdentifier>', description: 'Tax identifiers (GSTIN/PAN/VAT/OTHER). See **TaxIdentifier** in the request body below.' },
        { name: 'tags', type: 'object<string, string>', description: 'Free-form key/value tags.' },
      ],
      example: CUSTOMER,
    },
    endpoints: [
      {
        id: 'create-customer',
        method: 'POST',
        path: '/app/customer/create',
        title: 'Create a customer',
        description: 'Creates a new customer. The response is wrapped in the standard `{error, message, data}` envelope.',
        requestBody: [
          { name: 'buyerId', type: 'string', required: true, description: 'Your own unique identifier for the customer — typically a CRM ID, ERP code, or similar. Must be a non-empty string and unique within your account; you can\'t create two customers with the same `buyerId`.' },
          { name: 'name', type: 'string', required: true, description: 'Legal name of the customer. Must be a non-empty string. Once set, it can\'t be changed.' },
          { name: 'alias', type: 'string', required: false, description: 'Alternate identifier or display name for the customer. Can be changed later. If omitted, defaults to `name`.' },
          {
            name: 'taxIdentifiers',
            type: 'array<TaxIdentifier>',
            required: false,
            description: 'Tax identifiers (GSTIN, PAN, VAT, etc.) registered for this customer. Defaults to `[]`.',
            children: [
              { name: 'value', type: 'string', required: true, description: 'The identifier value (e.g. a 15-character GSTIN like `29ABCDE1234F1Z5`, or a 10-character PAN). GSTIN and PAN values are validated for format.' },
              { name: 'type', type: 'enum', required: true, enumRef: 'TaxIdentifierType', description: 'The kind of identifier. One of `GSTIN`, `PAN`, `VAT`, `OTHER`.' },
            ],
          },
          {
            name: 'registeredAddress',
            type: 'Address | null',
            required: false,
            description: 'Registered or billing address for the customer. Defaults to `null`. See [Addresses](api-reference/addresses) for the field structure.',
          },
          { name: 'countryCode', type: 'string', required: false, description: 'Two-letter country code for the customer\'s country (e.g. `IN` for India, `US` for the United States, `GB` for the United Kingdom). Used to populate the `country` field. Defaults to `IN`.' },
          { name: 'stateCode', type: 'string', required: false, description: 'Two-digit GST state code identifying the customer\'s state under India\'s GST system (e.g. `29` for Karnataka, `27` for Maharashtra, `07` for Delhi). Matches the first two digits of the customer\'s GSTIN. Required for Indian customers — it\'s used to determine the correct GST treatment (CGST + SGST for intra-state, IGST for inter-state) on their invoices.' },
          { name: 'currency', type: 'string', required: false, description: 'Three-letter ISO currency code (e.g. `INR`, `USD`, `EUR`). All invoices, credit notes, and other billing documents for this customer are issued in this currency. Defaults to `INR`.' },
          { name: 'tags', type: 'object<string, string>', required: false, description: 'Free-form key/value tags. Defaults to `{}`.' },
        ],
        requestExample: {
          buyerId: 'CUST-1042',
          name: 'Acme Industries Pvt Ltd',
          alias: 'Acme',
          taxIdentifiers: [
            { value: '29ABCDE1234F1Z5', type: 'GSTIN' },
          ],
          registeredAddress: {
            line1: '221B Baker Street',
            city: 'Bengaluru',
            state: 'Karnataka',
            stateCode: '29',
            country: 'India',
            zipCode: '560001',
          },
          countryCode: 'IN',
          stateCode: '29',
          currency: 'INR',
          tags: { segment: 'enterprise' },
        },
        responseFields: [
          { name: 'data', type: 'Customer', description: 'The created customer. Auto-populated fields (`id`, `country`, `alias`) are filled in for you if omitted in the request.' },
        ],
        responseExamples: [
          { label: '201 Created', status: 201, body: envelope('Customer created successfully', CUSTOMER) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', '29ABCDE1234F1Z is not a valid value for tax identifier of type GSTIN') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a session token or an API key.') },
        ],
        errors: [
          { status: 400, when: '`buyerId` is missing or not a non-empty string.' },
          { status: 400, when: '`name` is missing or not a non-empty string.' },
          { status: 400, when: '`taxIdentifiers` is not a list of `{value, type}` objects, or `type` is outside `GSTIN | PAN | VAT | OTHER`.' },
          { status: 400, when: 'A GSTIN or PAN `value` fails format validation.' },
          { status: 400, when: '`registeredAddress` is provided but is not an object, or `tags` is not a `{string: string}` map.' },
          { status: 400, when: '`countryCode` is not a recognized two-letter country code, or `stateCode` is not a valid GST state code.' },
          { status: 403, when: 'Missing or invalid session cookie / API key.' },
        ],
      },
    ],
  },

  contacts: {
    id: 'contacts',
    label: 'Contacts',
    tagline: 'People at the customer you can reach',
    description: 'A contact represents a person at the customer organization — typically someone you can reach for billing communication, like an accounts-payable owner, finance lead, or procurement contact. Contacts are nested under a customer.',
    object: {
      name: 'The contact object',
      fields: [
        { name: 'id', type: 'string', description: 'Auto-generated ID for the contact. Created by Valyx and returned in the response — you don\'t need to send this.' },
        { name: 'customerId', type: 'string', description: 'ID of the customer this contact belongs to. Set automatically from the URL path — you don\'t need to send it in the body.' },
        { name: 'name', type: 'string', description: 'Contact\'s full name.' },
        { name: 'role', type: 'string', description: 'Role of the contact at the customer (e.g. `AP`, `Finance Head`, `Procurement`).' },
        { name: 'primaryPhone', type: 'string | null', description: 'Primary phone number for this contact. Send it in international E.164 format — a leading `+`, then the country code, then the subscriber number, with no spaces or separators (e.g. `+919876543210` for India, `+14155551234` for the US).' },
        { name: 'primaryEmail', type: 'string | null', description: 'Primary email address for this contact. Must be a syntactically valid email address.' },
      ],
      example: CONTACT,
    },
    endpoints: [
      {
        id: 'create-customer-contacts',
        method: 'POST',
        path: '/app/customer/{customer_id}/contact/create',
        title: 'Create contacts on a customer',
        description: 'Adds one or more contacts to an existing customer in a single call.\n\n**Atomic validation.** Every contact in the request body is validated up front. If any one entry is invalid, the whole request is rejected with a `400` and nothing is created — there\'s no half-saved batch.',
        pathParams: [
          { name: 'customer_id', type: 'string', description: 'ID of the customer to add contacts to. Must belong to your account.' },
        ],
        requestBody: [
          {
            name: 'contacts',
            type: 'array<Contact>',
            required: true,
            description: 'Non-empty list of contacts to create. Two contacts in the same call can\'t share the same `name` + `role` pair (compared case-insensitively after trimming whitespace).',
            children: [
              { name: 'name', type: 'string', required: true, description: 'Contact\'s full name. Must be a non-empty string.' },
              { name: 'role', type: 'string', required: true, description: 'Role of the contact at the customer (e.g. `AP`, `Finance Head`, `Procurement`). Must be a non-empty string.' },
              { name: 'primaryPhone', type: 'string', required: false, description: 'Primary phone number for this contact. Send it in international E.164 format — a leading `+`, then the country code, then the subscriber number, with no spaces or separators (e.g. `+919876543210` for India, `+14155551234` for the US).' },
              { name: 'primaryEmail', type: 'string', required: false, description: 'Primary email address for this contact. Must be a syntactically valid email address.' },
            ],
          },
        ],
        requestExample: {
          contacts: [
            { name: 'Priya Sharma', role: 'AP', primaryPhone: '+919876543210', primaryEmail: 'priya@acme.example' },
            { name: 'Rahul Kumar', role: 'Finance Head', primaryEmail: 'rahul@acme.example' },
          ],
        },
        responseFields: [
          { name: 'data', type: 'array<Contact>', description: 'The contacts that were created, in the same order as the request.' },
        ],
        responseExamples: [
          { label: '201 Created', status: 201, body: envelope('Created 2 contact(s) successfully', [CONTACT, CONTACT_2]) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('VALUE_ERROR', "contacts[1] duplicates an earlier contact with the same name='Alice' and role='AP'.") },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a session token or an API key.') },
        ],
        errors: [
          { status: 400, when: '`contacts` is missing, not a list, or empty.' },
          { status: 400, when: 'A contact entry is missing `name` or `role`, or either is not a non-empty string.' },
          { status: 400, when: 'Two contacts in the request share the same `name` + `role` pair.' },
          { status: 400, when: '`primaryPhone` or `primaryEmail` fails format validation.' },
          { status: 400, when: 'No customer exists with the given `customer_id` for your account.' },
          { status: 403, when: 'Missing or invalid session cookie / API key.' },
          { status: 403, when: 'The `customer_id` belongs to a different account than yours.' },
        ],
      },
    ],
  },

  invoices: {
    id: 'invoices',
    label: 'Invoices',
    tagline: 'Bills you issue to customers',
    description: 'An invoice is a bill issued to a customer for goods or services. Invoices have line items, taxes, and totals — Valyx stores the amounts you supply as-is and does not recompute or aggregate them. Each invoice has a lifecycle status (`DRAFT` or `FINALIZED`) and tracks payment progress separately.',
    object: {
      name: 'The invoice object',
      fields: [
        { name: 'id', type: 'string', description: 'Auto-generated ID for the invoice. Created by Valyx and returned in the response — you don\'t need to send this.' },
        { name: 'invoiceNumber', type: 'string', description: 'Your invoice number, in your account\'s prescribed format (e.g. `INV-2026-0042`). Must be unique within your account.' },
        { name: 'customerId', type: 'string', description: 'ID of the customer this invoice is for. Either supplied directly when creating, or resolved from `customerDetails` (lookup by `buyerId`, or new customer creation).' },
        { name: 'invoiceDate', type: 'string', description: 'Date the invoice was issued, in `YYYY-MM-DD` format.' },
        { name: 'invoiceDueDate', type: 'string | null', description: 'Date payment is due, in `YYYY-MM-DD` format.' },
        { name: 'promiseToPayDate', type: 'string | null', description: 'Date the customer has promised to pay, in `YYYY-MM-DD` format.' },
        { name: 'invoiceCurrency', type: 'string', description: 'Three-letter ISO currency code the invoice is denominated in (e.g. `INR`, `USD`).' },
        { name: 'exchangeRate', type: 'number | null', description: 'Exchange rate from `invoiceCurrency` to your account\'s base currency, locked at creation time. `1.0` when the invoice is in your base currency.' },
        { name: 'invoiceSubtotal', type: 'number', description: 'Pre-tax total — the sum of line-item subtotals.' },
        { name: 'invoiceTax', type: 'Tax | null', description: 'Invoice-level tax breakdown. See **Tax** in the request body below.' },
        { name: 'invoiceTotal', type: 'number', description: 'Final invoice total (subtotal + tax − discounts). Derived by Valyx; if you supplied a value on create, it had to match this derived value or the request was rejected.' },
        { name: 'discount', type: 'Discount | null', description: 'Invoice-level discount, if applied. See **Discount** in the request body below.' },
        { name: 'placeOfSupply', type: 'string | null', description: 'Free-form label describing the place of supply for tax purposes.' },
        { name: 'invoiceType', type: 'string | null', description: 'Free-form invoice type label.' },
        { name: 'invoiceDocumentType', type: 'string', description: 'Document type. Currently always `INVOICE`.' },
        { name: 'lifecycleStatus', type: 'enum', description: 'Lifecycle status. One of `DRAFT` (created but not yet issued) or `FINALIZED` (issued to the customer).' },
        { name: 'invoiceStatus', type: 'enum', description: 'Current state of the invoice (e.g. `DUE`, `OVERDUE`, `PAID`). Auto-populated by Valyx.' },
        { name: 'paymentStatus', type: 'enum', description: 'Payment progress. One of `UNPAID`, `PARTIALLY_PAID`, `PAID`. Auto-populated by Valyx.' },
        { name: 'lineItems', type: 'array<LineItem>', description: 'The invoice line items. See **LineItem** in the request body below for the field shape.' },
        { name: 'tags', type: 'object<string, string>', description: 'Free-form key/value tags.' },
        { name: 'salesDate', type: 'string | null', description: 'Date of the underlying sale or order, in `YYYY-MM-DD` format. `null` if not provided on create.' },
        { name: 'salesOrderNumber', type: 'string | null', description: 'Your sales order number associated with the invoice.' },
        { name: 'salesOrderId', type: 'string | null', description: 'Your ERP\'s identifier for the sales order.' },
        { name: 'generalTerms', type: 'string | null', description: 'Free-text general terms.' },
        { name: 'paymentTerms', type: 'string | null', description: 'Free-text payment terms (e.g. `Net 30`).' },
        { name: 'externalUrl', type: 'string | null', description: 'External link associated with the invoice.' },
        { name: 'billingAddress', type: 'AddressWithPhone | null', description: 'Billing address. Always present in the response — `null` when not supplied. Includes a derived `pan` when `gstin` is set.' },
        { name: 'shippingAddress', type: 'AddressWithPhone | null', description: 'Shipping address. Always present in the response — `null` when not supplied.' },
        { name: 'sellerDetails', type: 'SellerDetails | null', description: 'Seller snapshot — website, address (with phone and GSTIN), and bank account.' },
        { name: 'buyerDetails', type: 'BuyerDetails | null', description: 'Buyer snapshot — address and GSTIN.' },
      ],
      example: INVOICE,
    },
    endpoints: [
      {
        id: 'create-invoice',
        method: 'POST',
        path: '/app/invoice/create',
        title: 'Create an invoice',
        description: 'Creates an invoice for a customer.',
        requestBody: [
          // ── Customer ────────────────────────────────────────────────────
          { group: 'Customer', name: 'customerId', type: 'string', required: false, description: 'ID of an existing customer to bill. Provide this OR `customerDetails`, but not both.' },
          {
            group: 'Customer',
            name: 'customerDetails',
            type: 'CustomerDetails',
            required: false,
            description: 'Inline customer payload. Valyx looks up an existing customer by `buyerId`; if a match exists it\'s linked (other fields are ignored), otherwise a new customer is created from these fields. Provide this OR `customerId`, but not both.',
            children: [
              { name: 'buyerId', type: 'string', required: true, description: 'Your unique identifier for the customer. Used as the lookup key.' },
              { name: 'name', type: 'string', required: false, description: 'Legal name of the customer. Required when creating a new customer; ignored when linking to an existing one.' },
              { name: 'alias', type: 'string', required: false, description: 'Alternate identifier or display name. If omitted, defaults to `name`.' },
              { name: 'taxIdentifiers', type: 'array<TaxIdentifier>', required: false, description: 'Tax identifiers. Each entry is `{ value: string, type: "GSTIN" | "PAN" | "VAT" | "OTHER" }`.' },
              { name: 'registeredAddress', type: 'Address', required: false, description: 'Registered or billing address. See [Addresses](api-reference/addresses) for the field structure.' },
              { name: 'countryCode', type: 'string', required: false, description: 'Two-letter country code (e.g. `IN`, `US`). Defaults to `IN`.' },
              { name: 'stateCode', type: 'string', required: false, description: 'Two-digit GST state code. Required for Indian customers.' },
              { name: 'tags', type: 'object<string, string>', required: false, description: 'Free-form key/value tags for the customer.' },
            ],
          },

          // ── Invoice details ─────────────────────────────────────────────
          { group: 'Invoice details', name: 'invoiceNumber', type: 'string', required: true, description: 'Your invoice number in your account\'s prescribed format. Must be a non-empty string and unique within your account.' },
          { group: 'Invoice details', name: 'invoiceDate', type: 'string', required: true, description: 'Date the invoice is issued, in `YYYY-MM-DD` format.' },
          { group: 'Invoice details', name: 'invoiceDueDate', type: 'string', required: false, description: 'Date payment is due, in `YYYY-MM-DD` format.' },
          { group: 'Invoice details', name: 'promiseToPayDate', type: 'string', required: false, description: 'Date the customer has promised to pay, in `YYYY-MM-DD` format.' },
          { group: 'Invoice details', name: 'invoiceCurrency', type: 'string', required: false, description: 'Three-letter ISO currency code (e.g. `INR`, `USD`, `EUR`). Must match the linked customer\'s currency. Defaults to `INR`.' },
          { group: 'Invoice details', name: 'exchangeRate', type: 'number', required: false, description: 'Exchange rate from `invoiceCurrency` to your account\'s base currency, locked at creation. Required when `invoiceCurrency` differs from your base currency.' },
          { group: 'Invoice details', name: 'isDraft', type: 'boolean', required: false, description: 'When `true`, creates the invoice in `DRAFT` lifecycle. Defaults to `false` (creates a `FINALIZED` invoice).' },
          { group: 'Invoice details', name: 'invoiceType', type: 'string', required: false, description: 'Free-form invoice type label.' },
          { group: 'Invoice details', name: 'tags', type: 'object<string, string>', required: false, description: 'Free-form key/value tags. Defaults to `{}`.' },

          // ── Amounts & taxes ─────────────────────────────────────────────
          { group: 'Amounts & taxes', name: 'invoiceSubtotal', type: 'number', required: true, description: 'Pre-tax total. Must be non-negative. Should equal the sum of `lineItems[*].subTotal`.' },
          {
            group: 'Amounts & taxes',
            name: 'invoiceTax',
            type: 'Tax',
            required: false,
            description: 'Invoice-level tax aggregate. See [Tax modelling](api-reference/tax-modelling) for the full structure, the intra-state vs inter-state GST rules, and worked examples.',
            children: TAX_FIELDS,
          },
          { group: 'Amounts & taxes', name: 'invoiceTotal', type: 'number', required: false, description: 'Optional expected invoice total (subtotal + tax − discounts). When supplied, Valyx derives the real total and returns a `400` if the derived value doesn\'t match — useful as a sanity check on your computation.' },
          {
            group: 'Amounts & taxes',
            name: 'discount',
            type: 'Discount',
            required: false,
            description: 'Invoice-level discount, if applied. Send the absolute amount only — the percentage is auto-calculated by Valyx and returned in the response.',
            children: [
              { name: 'amount', type: 'number', required: false, description: 'Absolute discount amount.' },
            ],
          },
          { group: 'Amounts & taxes', name: 'placeOfSupply', type: 'string', required: false, description: 'Free-form label describing the place of supply for tax purposes.' },
          { group: 'Amounts & taxes', name: 'supplyStateCode', type: 'string', required: false, description: 'Two-digit GST state code for the place of supply. Used by GST validation on finalized invoices.' },

          // ── Line items ──────────────────────────────────────────────────
          {
            group: 'Line items',
            name: 'lineItems',
            type: 'array<LineItem>',
            required: true,
            description: 'Non-empty list of line items on the invoice.',
            children: [
              { name: 'name', type: 'string', required: true, description: 'Line item label (e.g. `Consulting hours`). Must be a non-empty string.' },
              { name: 'description', type: 'string', required: false, description: 'Longer description of the line item.' },
              { name: 'hsnOrSacCode', type: 'string', required: false, description: 'HSN code (for goods) or SAC code (for services), if applicable.' },
              { name: 'quantity', type: 'number', required: false, description: 'Quantity. Defaults to `1.0`. Must be non-negative.' },
              { name: 'unit', type: 'string', required: false, description: 'Unit of measure (UQC code, e.g. `NOS`, `KGS`, `MTR`).' },
              { name: 'pricePerItem', type: 'number', required: false, description: 'Price per unit. Must be non-negative.' },
              { name: 'subTotal', type: 'number', required: true, description: 'Pre-tax line total. Must be non-negative.' },
              { name: 'taxRate', type: 'number', required: true, description: 'Tax rate as a percentage (e.g. `18` for 18%). Must be non-negative.' },
              {
                name: 'tax',
                type: 'Tax',
                required: false,
                description: 'Line-level tax aggregate. Same shape as `invoiceTax`. See [Tax modelling](api-reference/tax-modelling) for details.',
                children: TAX_FIELDS,
              },
              {
                name: 'discount',
                type: 'Discount',
                required: false,
                description: 'Per-line discount. Send the absolute amount only — the percentage is auto-calculated by Valyx and returned in the response.',
                children: [
                  { name: 'amount', type: 'number', required: false, description: 'Absolute discount amount.' },
                ],
              },
              { name: 'lineItemType', type: 'string', required: false, description: 'Type of line item. One of `GOODS` or `SERVICE`.' },
              { name: 'erpId', type: 'string', required: false, description: 'External ERP identifier for this line.' },
            ],
          },

          // ── Sales context ───────────────────────────────────────────────
          { group: 'Sales context', name: 'salesDate', type: 'string', required: false, description: 'Date of the underlying sale or order, in `YYYY-MM-DD` format.' },
          { group: 'Sales context', name: 'salesOrderNumber', type: 'string', required: false, description: 'Your sales order number associated with this invoice.' },
          { group: 'Sales context', name: 'salesOrderId', type: 'string', required: false, description: 'Your ERP\'s identifier for the sales order.' },
          { group: 'Sales context', name: 'generalTerms', type: 'string', required: false, description: 'Free-text general terms to display on the invoice.' },
          { group: 'Sales context', name: 'paymentTerms', type: 'string', required: false, description: 'Free-text payment terms (e.g. `Net 30`).' },
          { group: 'Sales context', name: 'externalUrl', type: 'string', required: false, description: 'External link associated with the invoice (e.g. a portal URL).' },

          // ── Addresses & parties ─────────────────────────────────────────
          {
            group: 'Addresses & parties',
            name: 'billingAddress',
            type: 'AddressWithPhone',
            required: false,
            description: 'Billing address for this invoice. Overrides the customer\'s registered address when supplied. See [Addresses](api-reference/addresses) for the field structure.',
          },
          {
            group: 'Addresses & parties',
            name: 'shippingAddress',
            type: 'AddressWithPhone',
            required: false,
            description: 'Shipping address for this invoice. See [Addresses](api-reference/addresses) for the field structure.',
          },
          {
            group: 'Addresses & parties',
            name: 'sellerDetails',
            type: 'SellerDetails',
            required: false,
            description: 'Seller snapshot. The seller\'s `primaryPhone` and `gstin` live on `address` (not duplicated at the top level — values sent there are ignored).',
            children: [
              { name: 'website', type: 'string', required: false, description: 'Seller\'s website URL.' },
              {
                name: 'address',
                type: 'AddressWithPhone',
                required: false,
                description: 'Seller\'s address — carries the seller\'s phone and GSTIN. See [Addresses](api-reference/addresses) for the field structure.',
              },
              {
                name: 'bankAccountDetails',
                type: 'BankAccountDetails',
                required: false,
                description: 'Seller\'s bank account for payments.',
                children: BANK_ACCOUNT_FIELDS,
              },
            ],
          },
          {
            group: 'Addresses & parties',
            name: 'buyerDetails',
            type: 'BuyerDetails',
            required: false,
            description: 'Buyer snapshot — address and GSTIN.',
            children: [
              {
                name: 'address',
                type: 'AddressWithPhone',
                required: false,
                description: 'Buyer\'s address. See [Addresses](api-reference/addresses) for the field structure.',
              },
              { name: 'gstin', type: 'string', required: false, description: 'Buyer\'s GSTIN. The PAN in the response is auto-derived from this.' },
            ],
          },
        ],
        requestExample: {
          customerId: 'cust_29_CUST-1042',
          invoiceNumber: 'INV-2026-0042',
          invoiceDate: '2026-05-08',
          invoiceDueDate: '2026-06-07',
          invoiceSubtotal: 100000.0,
          invoiceTax: {
            gstTax: {
              cgst: 9000.0,
              sgst: 9000.0,
            },
          },
          lineItems: [
            {
              name: 'Consulting hours',
              subTotal: 100000.0,
              taxRate: 18.0,
              tax: {
                gstTax: {
                  cgst: 9000.0,
                  sgst: 9000.0,
                },
              },
            },
          ],
          salesDate: '2026-05-01',
          salesOrderNumber: 'SO-2026-0042',
          paymentTerms: 'Net 30',
          billingAddress: {
            line1: '1 Bill Street',
            city: 'Bengaluru',
            stateCode: '29',
            primaryPhone: '+919876543210',
          },
          sellerDetails: {
            website: 'https://acme.example',
            address: {
              line1: 'Seller Plaza',
              city: 'Bengaluru',
              stateCode: '29',
              gstin: '29AABCU9603R1Z2',
              primaryPhone: '+919999999999',
            },
            bankAccountDetails: {
              accountHolderName: 'Acme Industries',
              bankAccountNo: '0123456789',
              ifscCode: 'HDFC0000123',
              bankName: 'HDFC Bank',
            },
          },
          buyerDetails: {
            address: { line1: 'Buyer Lane', stateCode: '29' },
            gstin: '29ABCDE1234F1Z5',
          },
        },
        responseFields: [
          { name: 'data', type: 'Invoice', description: 'The created invoice. Auto-populated fields (`id`, `customerId` when using `customerDetails`, `lifecycleStatus`, `invoiceStatus`, `paymentStatus`) are filled in for you.' },
        ],
        responseExamples: [
          { label: '201 Created', status: 201, body: envelope('Invoice created successfully', INVOICE) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('VALUE_ERROR', 'Provide either customerId or customerDetails, not both.') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a session token or an API key.') },
        ],
        errors: [
          { status: 400, when: 'Neither `customerId` nor `customerDetails` is provided, or both are provided.' },
          { status: 400, when: '`customerDetails.buyerId` is missing or not a non-empty string.' },
          { status: 400, when: '`invoiceNumber` is missing, blank, or duplicates an existing invoice in your account.' },
          { status: 400, when: '`invoiceDate`, `invoiceDueDate`, or `promiseToPayDate` is not a `YYYY-MM-DD` string.' },
          { status: 400, when: '`invoiceSubtotal`, `invoiceTotal`, or any tax / quantity / price field is negative or non-numeric.' },
          { status: 400, when: '`invoiceTotal` was supplied but doesn\'t match the value Valyx derives from `invoiceSubtotal`, `invoiceTax`, and `discount`.' },
          { status: 400, when: '`invoiceCurrency` is not a 3-character ISO code, or doesn\'t match the linked customer\'s currency.' },
          { status: 400, when: '`lineItems` is missing, empty, or any item is missing `name`, `subTotal`, or `taxRate`.' },
          { status: 400, when: '`lineItems[*].lineItemType` is provided but is not `GOODS` or `SERVICE`.' },
          { status: 400, when: '`isDraft` is not a boolean, or `tags` is not a `{string: string}` map.' },
          { status: 400, when: '`salesDate` is provided but is not a `YYYY-MM-DD` string.' },
          { status: 400, when: '`billingAddress`, `shippingAddress`, `sellerDetails`, or `buyerDetails` is provided but is not an object.' },
          { status: 400, when: '`sellerDetails.address`, `sellerDetails.bankAccountDetails`, or `buyerDetails.address` is provided but is not an object.' },
          { status: 400, when: '`salesOrderNumber`, `salesOrderId`, `generalTerms`, `paymentTerms`, or `externalUrl` is provided but is not a string.' },
          { status: 400, when: '`customerId` does not exist for your account.' },
          { status: 403, when: 'Missing or invalid session cookie / API key.' },
          { status: 403, when: '`customerId` belongs to a different account than yours.' },
        ],
      },
    ],
  },

  'custom-fields': {
    id: 'custom-fields',
    label: 'Custom Fields',
    tagline: 'Arbitrary typed metadata on documents',
    description: 'Custom fields let you attach typed metadata to any document — invoices, customers, and more. The system is two-layered: **property definitions** declare what fields exist for a given document type (company-scoped), and **property values** store the actual value for each document instance.\n\nAll operations are scoped to the authenticated company. Definitions from other companies are invisible and cannot be referenced.',
    object: {
      name: 'The property definition object',
      fields: [
        { name: 'id', type: 'string', description: 'Auto-generated ID for the property definition.' },
        { name: 'propertyName', type: 'string', description: 'Human-readable display name shown in the UI (e.g. `"Product Line"`).' },
        { name: 'propertyKey', type: 'string', description: 'Immutable internal key used for lookups (e.g. `"product_line"`). Unique per company + document type. Cannot contain spaces.' },
        { name: 'documentType', type: 'enum', description: 'Document type this property belongs to. One of `INVOICE`, `DEBIT_NOTE`, `CREDIT_NOTE`, `RECEIPT`, `CUSTOMER`, `BANK_ACCOUNT`, `CONTRACT`.' },
        { name: 'dataType', type: 'enum', description: 'Data type for property values. One of `STRING`, `NUMERIC`, `DATE`, `BOOLEAN`.' },
        { name: 'isMandatory', type: 'boolean', description: 'Whether the field is required on each document.' },
        { name: 'defaultValue', type: 'string | null', description: 'Default value stored as a string, or `null` if not set.' },
      ],
      example: PROPERTY_DEFINITION,
    },
    endpoints: [
      {
        id: 'create-custom-field',
        method: 'POST',
        path: '/customFields/create',
        title: 'Create a property definition',
        description: 'Declares a new custom field for a given document type. Once created, the `propertyKey` is immutable and scoped to your company + document type — you cannot create two definitions with the same key.',
        requestBody: [
          { name: 'documentType', type: 'enum', required: true, description: 'Document type this property belongs to. One of `INVOICE`, `DEBIT_NOTE`, `CREDIT_NOTE`, `RECEIPT`, `CUSTOMER`, `BANK_ACCOUNT`, `CONTRACT`.' },
          { name: 'propertyName', type: 'string', required: true, description: 'Human-readable display name (e.g. `"Product Line"`). Must be non-empty after trimming.' },
          { name: 'propertyKey', type: 'string', required: true, description: 'Immutable internal key for lookups (e.g. `"product_line"`). Must be non-empty with no spaces. Case-sensitive and unique per company + document type.' },
          { name: 'dataType', type: 'enum', required: true, description: 'Value data type. One of `STRING`, `NUMERIC`, `DATE`, `BOOLEAN`.' },
          { name: 'isMandatory', type: 'boolean', required: false, description: 'Whether the field is required. Defaults to `false`.' },
          { name: 'defaultValue', type: 'string', required: false, description: 'Default value stored as a string. Optional.' },
          {
            name: 'typeConfig',
            type: 'object',
            required: false,
            description: 'Optional constraints on accepted values. Omit or pass `null` for no constraints.',
            children: [
              { name: 'allowedValues', type: 'string[]', required: false, description: 'Restricts accepted values to this explicit list. Applicable to `STRING` data types. Must be non-empty if provided.' },
              { name: 'minValue', type: 'number', required: false, description: 'Inclusive lower bound for `NUMERIC` fields. Value must be ≥ this.' },
              { name: 'maxValue', type: 'number', required: false, description: 'Inclusive upper bound for `NUMERIC` fields. Value must be ≤ this.' },
            ],
          },
        ],
        requestExample: {
          documentType: 'INVOICE',
          propertyName: 'Product Line',
          propertyKey: 'product_line',
          dataType: 'STRING',
          typeConfig: {
            allowedValues: ['North', 'South', 'East', 'West'],
          },
        },
        responseFields: [
          { name: 'data', type: 'PropertyDefinition', description: 'The created property definition.' },
        ],
        responseExamples: [
          { label: '201 Created', status: 201, body: envelope('Document property definition created successfully', PROPERTY_DEFINITION) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValidationError', 'propertyKey cannot contain spaces') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a session token or an API key.') },
        ],
        errors: [
          { status: 400, when: '`documentType` is missing or not a valid enum value.' },
          { status: 400, when: '`propertyName` is missing or blank.' },
          { status: 400, when: '`propertyKey` is missing, blank, or contains spaces.' },
          { status: 400, when: '`dataType` is missing or not a valid enum value.' },
          { status: 400, when: '`typeConfig.allowedValues` is provided but is an empty array.' },
          { status: 400, when: '`typeConfig.minValue` or `typeConfig.maxValue` is provided but is not a number.' },
          { status: 403, when: 'Missing or invalid credentials.' },
        ],
      },
      {
        id: 'set-custom-field-value',
        method: 'POST',
        path: '/customFields/value',
        title: 'Set a property value',
        description: 'Upserts a property value on a specific document. If the property already has a value for this document, it is overwritten. Pass `null` as `value` to explicitly clear/unset the property — constraint validation is skipped when setting `null`.\n\nProvide either `documentId` (the internal DB primary key) or `documentNumber` (a business-level identifier like `"INV-2025-001"`). When both are supplied, `documentId` takes precedence. `documentNumber` is only supported for `documentType = INVOICE`.',
        requestBody: [
          { name: 'documentType', type: 'enum', required: true, description: 'Document type. One of `INVOICE`, `DEBIT_NOTE`, `CREDIT_NOTE`, `RECEIPT`, `CUSTOMER`, `BANK_ACCOUNT`, `CONTRACT`.' },
          { name: 'documentId', type: 'string', required: false, description: 'Internal DB ID of the document. Provide this **or** `documentNumber`. Takes precedence when both are supplied.' },
          { name: 'documentNumber', type: 'string', required: false, description: 'Business document number (e.g. `"INV-2025-001"`). Only supported for `documentType = INVOICE`. Provide this **or** `documentId`.' },
          { name: 'propertyKey', type: 'string', required: true, description: 'Key of the property definition to set. Must match a definition created via `POST /customFields/create`.' },
          { name: 'value', type: 'any | null', required: true, description: 'The value to store. Type should match the property\'s `dataType`. Pass JSON `null` to unset the property. The `value` key must be present in the payload — omitting it entirely is a validation error.' },
        ],
        requestExample: {
          documentType: 'INVOICE',
          documentId: 'inv_db_id_xyz',
          propertyKey: 'product_line',
          value: 'North',
        },
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Property value set successfully', null) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValidationError', "Value 'Bad' is not allowed. Allowed values are: North, South, East, West") },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a session token or an API key.') },
        ],
        errors: [
          { status: 400, when: '`documentType` is missing or not a valid enum value.' },
          { status: 400, when: 'Both `documentId` and `documentNumber` are absent.' },
          { status: 400, when: '`propertyKey` is missing or blank.' },
          { status: 400, when: 'The `value` key is absent from the request body.' },
          { status: 400, when: 'No property definition found for `propertyKey` under this company + document type.' },
          { status: 400, when: 'Value violates `allowedValues`, `minValue`, or `maxValue` constraint.' },
          { status: 400, when: '`documentNumber` is provided but `documentType` is not `INVOICE`.' },
          { status: 400, when: '`documentNumber` is provided but no invoice with that number is found.' },
          { status: 403, when: 'Missing or invalid credentials.' },
        ],
      },
      {
        id: 'delete-custom-field-value',
        method: 'DELETE',
        path: '/customFields/value',
        title: 'Clear a property value',
        description: 'Clears (sets to `null`) the value of a property on a specific document. Equivalent to `POST /customFields/value` with `"value": null`, but uses query parameters instead of a request body.\n\nClearing a property that already has no value is a no-op — it succeeds without error.',
        queryParams: [
          { name: 'documentType', type: 'enum', required: true, description: 'Document type. One of `INVOICE`, `DEBIT_NOTE`, `CREDIT_NOTE`, `RECEIPT`, `CUSTOMER`, `BANK_ACCOUNT`, `CONTRACT`.' },
          { name: 'documentId', type: 'string', required: false, description: 'Internal DB ID of the document. Provide this **or** `documentNumber`. Takes precedence when both are supplied.' },
          { name: 'documentNumber', type: 'string', required: false, description: 'Business document number. Only supported for `documentType = INVOICE`. Provide this **or** `documentId`.' },
          { name: 'propertyKey', type: 'string', required: true, description: 'Key of the property to clear.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`DELETE /customFields/value?documentType=INVOICE&documentId=inv_db_id_xyz&propertyKey=product_line HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Property value cleared successfully', null) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValidationError', "No property definition found for key 'foo' on document type 'INVOICE'") },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a session token or an API key.') },
        ],
        errors: [
          { status: 400, when: '`documentType` is missing or not a valid enum value.' },
          { status: 400, when: 'Both `documentId` and `documentNumber` are absent.' },
          { status: 400, when: '`propertyKey` is missing or blank.' },
          { status: 400, when: 'No property definition found for `propertyKey` under this company + document type.' },
          { status: 400, when: '`documentNumber` is provided but `documentType` is not `INVOICE`.' },
          { status: 400, when: '`documentNumber` is provided but no invoice with that number is found.' },
          { status: 403, when: 'Missing or invalid credentials.' },
        ],
      },
      {
        id: 'get-custom-field-values',
        method: 'GET',
        path: '/customFields/values',
        title: 'Get property values for a document',
        description: 'Returns all **active** property definitions for this company + document type, each annotated with the current value for the specified document. Properties with no value set appear in the list with `value: null`.\n\nThe response always returns the full list of active definitions — even if no values have been stored yet. Deactivated definitions are excluded.\n\nProvide either `documentId` or `documentNumber`. When both are supplied, `documentId` takes precedence. `documentNumber` is only supported for `documentType = INVOICE`.',
        queryParams: [
          { name: 'documentType', type: 'enum', required: true, description: 'Document type. One of `INVOICE`, `DEBIT_NOTE`, `CREDIT_NOTE`, `RECEIPT`, `CUSTOMER`, `BANK_ACCOUNT`, `CONTRACT`.' },
          { name: 'documentId', type: 'string', required: false, description: 'Internal DB ID of the document. Provide this **or** `documentNumber`. Takes precedence when both are supplied.' },
          { name: 'documentNumber', type: 'string', required: false, description: 'Business document number. Only supported for `documentType = INVOICE`. Provide this **or** `documentId`.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`GET /customFields/values?documentType=INVOICE&documentId=inv_db_id_xyz HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data', type: 'array<PropertyValue>', description: 'All active definitions for this company + document type, each annotated with the current value.' },
          { name: 'data[*].propertyKey', type: 'string', description: 'Internal key of the property definition.' },
          { name: 'data[*].propertyName', type: 'string', description: 'Display name.' },
          { name: 'data[*].value', type: 'any | null', description: 'Current value for this document, or `null` if not set.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Property values retrieved successfully', PROPERTY_VALUES_RESPONSE) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValidationError', 'Please provide a documentType') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a session token or an API key.') },
        ],
        errors: [
          { status: 400, when: '`documentType` is missing or not a valid enum value.' },
          { status: 400, when: 'Both `documentId` and `documentNumber` are absent.' },
          { status: 400, when: '`documentNumber` is provided but `documentType` is not `INVOICE`.' },
          { status: 400, when: '`documentNumber` is provided but no invoice with that number is found.' },
          { status: 403, when: 'Missing or invalid credentials.' },
        ],
      },
    ],
  },

  products: {
    id: 'products',
    label: 'Products',
    tagline: 'The top-level billing entity',
    description: 'A product is the top-level billing entity — it groups fees, rate attributes, and usage attributes.\n\nEvery product has a `rateConfiguration` that controls whether multiple rate dimensions are supported. `SINGLE` means one price dimension and no rate attributes; `MULTIPLE` allows rate attributes to be created against the product.\n\n**Authentication.** Every endpoint in the `/billing/product` namespace requires the `X-Valyx-Signature` header. Session-cookie auth is **not** accepted on this namespace. `companyId` and `createdBy` are always sourced from the resolved API key, never from the request body.',
    object: {
      name: 'The product object',
      fields: [
        { name: 'id', type: 'string', description: 'Auto-generated ID for the product.' },
        { name: 'name', type: 'string', description: 'Display name for the product.' },
        { name: 'code', type: 'string', description: 'Caller-managed unique code. Auto-generated if you don\'t supply one at creation.' },
        { name: 'description', type: 'string | null', description: 'Free-text description.' },
        { name: 'hsnSac', type: 'string | null', description: 'HSN/SAC code (up to 8 digits).' },
        { name: 'usageBasedBilling', type: 'boolean', description: 'Whether the product supports usage-driven billing.' },
        { name: 'rateConfiguration', type: 'enum', enumRef: 'RateConfiguration', description: 'One of `SINGLE` or `MULTIPLE`.' },
        { name: 'usageAttributeCount', type: 'integer', description: 'Number of usage attributes defined on this product.' },
        { name: 'rateAttributeCount', type: 'integer', description: 'Number of rate attributes defined on this product.' },
        { name: 'feeCount', type: 'integer', description: 'Number of fees defined under this product.' },
        { name: 'lastModifiedBy', type: 'string | null', description: 'User ID of the last editor, or `null` if the product has never been updated.' },
        { name: 'lastUpdatedAt', type: 'string | null', description: 'Timestamp of the most recent update, or `null` if never updated.' },
      ],
      example: PRODUCT_ESTABLISHED,
    },
    endpoints: [
      {
        id: 'create-product',
        method: 'POST',
        path: '/billing/product',
        title: 'Create a product',
        description: 'Creates a product.\n\nTo copy an existing product instead of building one from scratch, set `isClone` to `true` and pass the product to copy in `sourceProductId`. Neither `rateConfiguration` nor `usageBasedBilling` may be set when cloning — the clone inherits both from the source product.\n\n`usageBasedBilling` **defaults to** `true`. Omitting it creates a usage-billing product, not a flat-fee one.\n\n`companyId` and `createdBy` are server-managed — do not include them in the body.',
        requestBody: [
          { name: 'name', type: 'string', required: true, description: 'Display name for the product. May contain letters, digits, spaces, hyphens, underscores, and `& ( ) . ,` — other characters are rejected.' },
          { name: 'code', type: 'string', required: false, description: 'Caller-managed unique code. Auto-generated if omitted. May contain only letters, digits, hyphens, and underscores.' },
          { name: 'description', type: 'string', required: false, description: 'Free-text description. Defaults to `null`.' },
          { name: 'hsnSac', type: 'string', required: false, description: 'HSN/SAC code — a numeric string of 2 to 8 digits. Defaults to `null`.' },
          { name: 'usageBasedBilling', type: 'boolean', required: false, description: 'Whether the product supports usage-driven billing. **Defaults to** `true` when omitted. Must not be set when `isClone` is `true`.' },
          { name: 'rateConfiguration', type: 'enum', required: false, enumRef: 'RateConfiguration', description: 'One of `SINGLE` or `MULTIPLE`. Defaults to `SINGLE`. Must not be set when `isClone` is `true`.' },
          { name: 'isClone', type: 'boolean', required: false, description: 'Pass `true` to clone from `sourceProductId`. Defaults to `false`.' },
          { name: 'sourceProductId', type: 'string', required: false, description: 'ID of the product to clone. Required when `isClone` is `true`, and must be omitted when `isClone` is `false` — sending it anyway is a `400`.' },
        ],
        requestExample: {
          name: 'Logistics Service',
          code: 'LOGI-001',
          description: 'Freight and logistics billing product',
          usageBasedBilling: true,
          rateConfiguration: 'MULTIPLE',
        },
        responseFields: [
          { name: 'data', type: 'Product', description: 'The created product. The `usageAttributeCount`, `rateAttributeCount`, and `feeCount` counters start at `0`.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Product created successfully', PRODUCT) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', 'rateConfiguration must not be set when isClone is true') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        extras: [
          {
            label: 'Clone',
            type: 'json',
            code:
`{
  "name": "Logistics Service - Copy",
  "isClone": true,
  "sourceProductId": "prod_abc123"
}`,
          },
        ],
        errors: [
          { status: 400, when: '`name` is missing or blank.' },
          { status: 400, when: '`name` contains characters outside letters, digits, spaces, hyphens, underscores, and `& ( ) . ,`' },
          { status: 400, when: '`code` contains characters outside letters, digits, hyphens, and underscores.' },
          { status: 400, when: '`hsnSac` is not a numeric string of 2 to 8 digits.' },
          { status: 400, when: '`rateConfiguration` is not one of `SINGLE` or `MULTIPLE`.' },
          { status: 400, when: '`isClone` is `true` but `sourceProductId` is absent.' },
          { status: 400, when: '`isClone` is `true` and `rateConfiguration` or `usageBasedBilling` is also set.' },
          { status: 400, when: '`isClone` is `false` but `sourceProductId` is set.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'list-products',
        method: 'POST',
        path: '/billing/product/filter',
        title: 'List products',
        description: 'Returns a paginated list of products for your company. Pagination is required — both `pageNumber` and `pageSize` must be supplied.\n\nThis endpoint uses `POST` rather than `GET` so that filter conditions can be sent in the request body.',
        requestBody: paginationFields('eq'),
        requestExample: {
          pageNumber: 1,
          pageSize: 20,
          orderBy: 'name',
          filterDicts: [],
        },
        responseFields: pageResponseFields('Product', 'The products on this page.'),
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Fetched products successfully', PRODUCT_LIST_PAGE) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: PAGINATION_ERRORS,
      },
      {
        id: 'get-product',
        method: 'GET',
        path: '/billing/product/{product_id}',
        title: 'Fetch a product',
        description: 'Returns a single product by ID. No request body is required.',
        pathParams: [
          { name: 'product_id', type: 'string', description: 'ID of the product to fetch. Must belong to your company.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`GET /billing/product/prod_abc123 HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data', type: 'Product', description: 'The requested product.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Fetched product successfully', PRODUCT_ESTABLISHED) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'update-product',
        method: 'PUT',
        path: '/billing/product/{product_id}',
        title: 'Update a product',
        description: `Updates a product field by field.\n\n${UPDATES_DESCRIPTION}\n\nTwo changes are blocked while dependent records exist: setting \`rateConfiguration\` to \`SINGLE\` while the product still has rate attributes, and setting \`usageBasedBilling\` to \`false\` while it still has usage attributes. Both return a \`400\` — delete the dependent attributes first.`,
        pathParams: [
          { name: 'product_id', type: 'string', description: 'ID of the product to update. Must belong to your company.' },
        ],
        requestBody: updatesBody([
          { name: 'name', type: 'string', required: false, description: 'New display name.' },
          { name: 'description', type: 'string', required: false, description: 'New description.' },
          { name: 'hsnSac', type: 'string', required: false, description: 'HSN/SAC code — a numeric string of 2 to 8 digits.' },
          { name: 'usageBasedBilling', type: 'boolean', required: false, description: 'Toggle usage-based billing. Cannot be set to `false` while usage attributes exist on the product.' },
          { name: 'rateConfiguration', type: 'enum', required: false, enumRef: 'RateConfiguration', description: 'One of `SINGLE` or `MULTIPLE`. Cannot be set to `SINGLE` while rate attributes exist on the product.' },
        ]),
        requestExample: {
          updates: {
            fields: ['name', 'description'],
            values: {
              name: 'Logistics Service v2',
              description: 'Updated description',
            },
          },
        },
        responseFields: [
          { name: 'data', type: 'Product', description: 'The updated product — same shape as the create and fetch responses.' },
        ],
        responseExamples: [
          {
            label: '200 OK',
            status: 200,
            body: envelope('Product updated successfully', {
              ...PRODUCT_ESTABLISHED,
              name: 'Logistics Service v2',
              description: 'Updated description',
              lastUpdatedAt: '2026-06-14T09:12:00',
            }),
          },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', 'rateConfiguration cannot be set to SINGLE while rate attributes exist') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`updates` is missing, or `updates.fields` / `updates.values` is absent.' },
          UPDATES_MISMATCH_ERROR,
          { status: 400, when: '`name` is blank, or contains characters outside letters, digits, spaces, hyphens, underscores, and `& ( ) . ,`' },
          { status: 400, when: '`hsnSac` is not a numeric string of 2 to 8 digits.' },
          { status: 400, when: '`rateConfiguration` is set to `SINGLE` while the product has existing rate attributes.' },
          { status: 400, when: '`usageBasedBilling` is set to `false` while the product has existing usage attributes.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'delete-product',
        method: 'DELETE',
        path: '/billing/product/{product_id}',
        title: 'Delete a product',
        description: 'Deletes a product. No request body is required.',
        pathParams: [
          { name: 'product_id', type: 'string', description: 'ID of the product to delete. Must belong to your company.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`DELETE /billing/product/prod_abc123 HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data.id', type: 'string', description: 'ID of the deleted product.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Product deleted successfully', { id: 'prod_abc123' }) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
    ],
  },

  'usage-attributes': {
    id: 'usage-attributes',
    label: 'Usage Attributes',
    tagline: 'Measurable dimensions of consumption',
    description: 'A usage attribute defines a measurable dimension of consumption for a product — for example "weight in kg" or "number of shipments".\n\nUsage attributes are referenced when ingesting usage events, and `METERED` fees use them to compute billed quantities: a fee\'s `usageAttributes[].apiCode` is the `apiName` of a usage attribute on that same product. They live under a product: `/billing/product/{product_id}/usageAttribute`.\n\nThe parent product must have `usageBasedBilling` enabled before any usage attribute can be added.\n\n**Authentication.** Every endpoint requires the `X-Valyx-Signature` header. Session-cookie auth is **not** accepted on this namespace. `companyId` and `createdBy` are always sourced from the resolved API key, never from the request body.',
    object: {
      name: 'The usage attribute object',
      fields: [
        { name: 'id', type: 'string', description: 'Auto-generated ID for the usage attribute.' },
        { name: 'productId', type: 'string', description: 'ID of the product this attribute belongs to. Set from the URL path — you don\'t send it in the body.' },
        { name: 'name', type: 'string', description: 'Display name for the attribute.' },
        { name: 'apiName', type: 'string', description: 'Machine-readable identifier used in usage event payloads.' },
        { name: 'unit', type: 'string', description: 'Unit of measurement (e.g. `kg`, `count`, `km`).' },
        { name: 'description', type: 'string | null', description: 'Free-text description.' },
        { name: 'createdAt', type: 'string', description: 'Creation timestamp.' },
      ],
      example: USAGE_ATTRIBUTE,
    },
    endpoints: [
      {
        id: 'create-usage-attribute',
        method: 'POST',
        path: '/billing/product/{product_id}/usageAttribute',
        title: 'Create a usage attribute',
        description: 'Adds a usage attribute to a product.\n\nThree preconditions apply: the product must have `usageBasedBilling` enabled, the `apiName` must be unique among the product\'s usage attributes, and the `name` must not collide with any of the product\'s rate attribute names.\n\n`companyId` and `createdBy` are server-managed — do not include them in the body.',
        pathParams: [
          { name: 'product_id', type: 'string', description: 'ID of the product to add the attribute to. Must belong to your company and have `usageBasedBilling` enabled.' },
        ],
        requestBody: [
          { name: 'name', type: 'string', required: true, description: 'Display name for the attribute. May contain letters, digits, spaces, hyphens, and underscores. Must not collide with a rate attribute name on the same product.' },
          { name: 'apiName', type: 'string', required: true, description: 'Machine-readable identifier used in usage event payloads, and the value a `METERED` fee references as `usageAttributes[].apiCode`. Must start with a letter or underscore and contain only letters, digits, and underscores. Unique per product.' },
          { name: 'unit', type: 'string', required: true, description: 'Unit of measurement (e.g. `kg`, `count`, `km`).' },
          { name: 'description', type: 'string', required: false, description: 'Free-text description.' },
        ],
        requestExample: {
          name: 'Shipment Weight',
          apiName: 'shipment_weight',
          unit: 'kg',
          description: 'Total weight of the shipment in kilograms',
        },
        responseFields: [
          { name: 'data', type: 'UsageAttribute', description: 'The created usage attribute. `createdAt` is `null` on the create response — fetch or list the attribute to read it.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Usage attribute created successfully', { ...USAGE_ATTRIBUTE, createdAt: null }) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', 'Usage attributes can only be added to products with usage-based billing enabled') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: 'A required field (`name`, `apiName`, `unit`) is missing or blank.' },
          { status: 400, when: '`name` contains characters outside letters, digits, spaces, hyphens, and underscores.' },
          { status: 400, when: '`apiName` does not start with a letter or underscore, or contains characters outside letters, digits, and underscores.' },
          { status: 400, when: 'The product does not have `usageBasedBilling` enabled.' },
          { status: 400, when: '`apiName` is already used by another usage attribute on this product.' },
          { status: 400, when: '`name` collides with a rate attribute name on this product.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'list-usage-attributes',
        method: 'POST',
        path: '/billing/product/{product_id}/usageAttribute/filter',
        title: 'List usage attributes',
        description: 'Returns a paginated list of the usage attributes on a product. Pagination is required — both `pageNumber` and `pageSize` must be supplied.\n\nThis endpoint uses `POST` rather than `GET` so that filter conditions can be sent in the request body.',
        pathParams: [
          { name: 'product_id', type: 'string', description: 'ID of the product whose attributes you want to list.' },
        ],
        requestBody: paginationFields('eq'),
        requestExample: {
          pageNumber: 1,
          pageSize: 20,
          filterDicts: [],
        },
        responseFields: pageResponseFields('UsageAttribute', 'The usage attributes on this page.'),
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Fetched usage attributes successfully', USAGE_ATTRIBUTE_LIST_PAGE) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: PAGINATION_ERRORS,
      },
      {
        id: 'update-usage-attribute',
        method: 'PUT',
        path: '/billing/product/{product_id}/usageAttribute/{usage_attribute_id}',
        title: 'Update a usage attribute',
        description: `Updates a usage attribute field by field.\n\n${UPDATES_DESCRIPTION}\n\n\`apiName\` is not updatable — usage event payloads reference it, so it stays fixed once created.`,
        pathParams: [
          { name: 'product_id', type: 'string', description: 'ID of the product the attribute belongs to.' },
          { name: 'usage_attribute_id', type: 'string', description: 'ID of the usage attribute to update.' },
        ],
        requestBody: updatesBody([
          { name: 'name', type: 'string', required: false, description: 'New display name.' },
          { name: 'description', type: 'string', required: false, description: 'New description.' },
          { name: 'unit', type: 'string', required: false, description: 'New unit of measurement.' },
        ]),
        requestExample: {
          updates: {
            fields: ['name', 'unit'],
            values: {
              name: 'Package Weight',
              unit: 'grams',
            },
          },
        },
        responseFields: [
          { name: 'data', type: 'UsageAttribute', description: 'The updated usage attribute.' },
        ],
        responseExamples: [
          {
            label: '200 OK',
            status: 200,
            body: envelope('Usage attribute updated successfully', {
              id: 'ua_abc123',
              productId: 'prod_xyz',
              name: 'Package Weight',
              apiName: 'shipment_weight',
              unit: 'grams',
              description: null,
              createdAt: '2026-06-01T09:00:00',
            }),
          },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`updates` is missing, or `updates.fields` / `updates.values` is absent.' },
          UPDATES_MISMATCH_ERROR,
          { status: 400, when: '`name` is blank, or contains characters outside letters, digits, spaces, hyphens, and underscores.' },
          { status: 400, when: '`unit` is provided but blank.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'delete-usage-attribute',
        method: 'DELETE',
        path: '/billing/product/{product_id}/usageAttribute/{usage_attribute_id}',
        title: 'Delete a usage attribute',
        description: 'Deletes a usage attribute. No request body is required.',
        pathParams: [
          { name: 'product_id', type: 'string', description: 'ID of the product the attribute belongs to.' },
          { name: 'usage_attribute_id', type: 'string', description: 'ID of the usage attribute to delete.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`DELETE /billing/product/prod_xyz/usageAttribute/ua_abc123 HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data.id', type: 'string', description: 'ID of the deleted usage attribute.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Usage attribute deleted successfully', { id: 'ua_abc123' }) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
    ],
  },

  'rate-attributes': {
    id: 'rate-attributes',
    label: 'Rate Attributes',
    tagline: 'Pricing dimensions on a product',
    description: 'A rate attribute defines a pricing dimension for a product — for example "origin region" or "weight class". Each attribute holds **groups**: sets of values that share the same rate.\n\nRate attributes can only be created when the product\'s `rateConfiguration` is `MULTIPLE`. Attempting to create one on a `SINGLE`-configured product returns a `400`.\n\n**Authentication.** Every endpoint requires the `X-Valyx-Signature` header. Session-cookie auth is **not** accepted on this namespace. `companyId` and `createdBy` are always sourced from the resolved API key, never from the request body.',
    object: {
      name: 'The rate attribute object',
      fields: [
        { name: 'id', type: 'string', description: 'Auto-generated ID for the rate attribute.' },
        { name: 'productId', type: 'string', description: 'ID of the product this attribute belongs to. Set from the URL path — you don\'t send it in the body.' },
        { name: 'name', type: 'string', description: 'Display name for the attribute.' },
        { name: 'apiName', type: 'string', description: 'Machine-readable identifier used in API payloads.' },
        { name: 'groups', type: 'string[][]', description: 'Value groups. Each inner array is a set of attribute values that share the same rate card.' },
        { name: 'description', type: 'string | null', description: 'Free-text description.' },
        { name: 'createdAt', type: 'string', description: 'Creation timestamp.' },
      ],
      example: RATE_ATTRIBUTE,
    },
    endpoints: [
      {
        id: 'get-rate-attributes',
        method: 'GET',
        path: '/billing/product/{product_id}/rateAttribute',
        title: 'Fetch all rate attributes',
        description: 'Returns every rate attribute for the product, **unpaginated**. Use `POST /billing/product/{product_id}/rateAttribute/filter` when you need paging or filtering.\n\nNo request body is required.',
        pathParams: [
          { name: 'product_id', type: 'string', description: 'ID of the product whose rate attributes you want.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`GET /billing/product/prod_xyz/rateAttribute HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data', type: 'array<RateAttribute>', description: 'Every rate attribute on the product.' },
        ],
        responseExamples: [
          {
            label: '200 OK',
            status: 200,
            body: envelope('Rate attributes fetched successfully', [
              { ...RATE_ATTRIBUTE_LISTED, description: 'Identifies the origin zone' },
            ]),
          },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'create-rate-attribute',
        method: 'POST',
        path: '/billing/product/{product_id}/rateAttribute',
        title: 'Create a rate attribute',
        description: 'Adds a pricing dimension to a product.\n\n**Group semantics.** Each inner array is a **group** — a set of attribute values that share the same rate card. `[["North", "South"], ["East", "West"]]` creates two groups: one for North/South routes and one for East/West routes.\n\nThe product\'s `rateConfiguration` must be `MULTIPLE`. `companyId` and `createdBy` are server-managed — do not include them in the body.',
        pathParams: [
          { name: 'product_id', type: 'string', description: 'ID of the product to add the attribute to. Must have `rateConfiguration` set to `MULTIPLE`.' },
        ],
        requestBody: [
          { name: 'name', type: 'string', required: true, description: 'Display name for the attribute. May contain letters, digits, spaces, hyphens, and underscores.' },
          { name: 'apiName', type: 'string', required: true, description: 'Machine-readable identifier used in API payloads. Must start with a letter or underscore and contain only letters, digits, and underscores. Unique per product.' },
          { name: 'groups', type: 'string[][]', required: true, description: 'List of value groups. Each group is a list of strings that map to the same rate. Must contain at least one group, no group may be empty, and **every value must be unique across all groups** — a value cannot appear in two groups.' },
          { name: 'description', type: 'string', required: false, description: 'Free-text description.' },
        ],
        requestExample: {
          name: 'Origin Region',
          apiName: 'origin_region',
          groups: [['North', 'South'], ['East', 'West']],
          description: 'Identifies the freight origin zone',
        },
        responseFields: [
          { name: 'data', type: 'RateAttribute', description: 'The created rate attribute.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Rate attribute created successfully', RATE_ATTRIBUTE) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', 'Rate attributes cannot be created on a product with rateConfiguration SINGLE') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: 'A required field (`name`, `apiName`, `groups`) is missing or blank.' },
          { status: 400, when: '`name` contains characters outside letters, digits, spaces, hyphens, and underscores.' },
          { status: 400, when: '`apiName` does not start with a letter or underscore, or contains characters outside letters, digits, and underscores.' },
          { status: 400, when: '`groups` is empty, or any group within it is empty.' },
          { status: 400, when: 'A value appears in more than one group.' },
          { status: 400, when: 'The product\'s `rateConfiguration` is `SINGLE`.' },
          { status: 400, when: '`apiName` is already used by another rate attribute on this product.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'list-rate-attributes',
        method: 'POST',
        path: '/billing/product/{product_id}/rateAttribute/filter',
        title: 'List rate attributes',
        description: 'Returns a paginated list of the rate attributes on a product. Pagination is required — both `pageNumber` and `pageSize` must be supplied.\n\nThis endpoint uses `POST` rather than `GET` so that filter conditions can be sent in the request body.',
        pathParams: [
          { name: 'product_id', type: 'string', description: 'ID of the product whose rate attributes you want to list.' },
        ],
        requestBody: paginationFields('eq'),
        requestExample: {
          pageNumber: 1,
          pageSize: 20,
          filterDicts: [],
        },
        responseFields: pageResponseFields('RateAttribute', 'The rate attributes on this page.'),
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Fetched rate attributes successfully', RATE_ATTRIBUTE_LIST_PAGE) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: PAGINATION_ERRORS,
      },
      {
        id: 'update-rate-attribute',
        method: 'PUT',
        path: '/billing/product/{product_id}/rateAttribute/{rate_attribute_id}',
        title: 'Update a rate attribute',
        description: `Updates a rate attribute field by field.\n\n${UPDATES_DESCRIPTION}\n\n\`groups\` is a **full replacement**, not a merge — send the complete set of groups you want to end up with. \`apiName\` is not updatable.`,
        pathParams: [
          { name: 'product_id', type: 'string', description: 'ID of the product the attribute belongs to.' },
          { name: 'rate_attribute_id', type: 'string', description: 'ID of the rate attribute to update.' },
        ],
        requestBody: updatesBody([
          { name: 'name', type: 'string', required: false, description: 'New display name.' },
          { name: 'description', type: 'string', required: false, description: 'New description.' },
          { name: 'groups', type: 'string[][]', required: false, description: 'Full replacement list of value groups. Same rules as on create: at least one group, no empty group, and every value unique across all groups.' },
        ]),
        requestExample: {
          updates: {
            fields: ['name', 'groups'],
            values: {
              name: 'Origin Zone',
              groups: [['North'], ['South'], ['East', 'West']],
            },
          },
        },
        responseFields: [
          { name: 'data', type: 'RateAttribute', description: 'The updated rate attribute.' },
        ],
        responseExamples: [
          {
            label: '200 OK',
            status: 200,
            body: envelope('Rate attribute updated successfully', {
              ...RATE_ATTRIBUTE_LISTED,
              name: 'Origin Zone',
              groups: [['North'], ['South'], ['East', 'West']],
            }),
          },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`updates` is missing, or `updates.fields` / `updates.values` is absent.' },
          UPDATES_MISMATCH_ERROR,
          { status: 400, when: '`name` is blank, or contains characters outside letters, digits, spaces, hyphens, and underscores.' },
          { status: 400, when: '`groups` is empty, or any group within it is empty.' },
          { status: 400, when: 'A value appears in more than one group.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'delete-rate-attribute',
        method: 'DELETE',
        path: '/billing/product/{product_id}/rateAttribute/{rate_attribute_id}',
        title: 'Delete a rate attribute',
        description: 'Deletes a rate attribute. No request body is required.',
        pathParams: [
          { name: 'product_id', type: 'string', description: 'ID of the product the attribute belongs to.' },
          { name: 'rate_attribute_id', type: 'string', description: 'ID of the rate attribute to delete.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`DELETE /billing/product/prod_xyz/rateAttribute/ra_abc123 HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data.id', type: 'string', description: 'ID of the deleted rate attribute.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Rate attribute deleted successfully', { id: 'ra_abc123' }) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
    ],
  },

  fees: {
    id: 'fees',
    label: 'Fees',
    tagline: 'Billable charges under a product',
    description: 'A fee is a billable charge linked to a product. It has a type (`FIXED` or `METERED`), optional currency settings, and optional usage-attribute weights.\n\nRate cards and currency-level overrides (tax, bill limits) are managed by separate endpoint groups — the `rateCards` array on a fee is read-only here.\n\n**Authentication.** Every endpoint in the `/billing/fee` namespace requires the `X-Valyx-Signature` header. Session-cookie auth is **not** accepted on this namespace. `companyId` and `createdBy` are always sourced from the resolved API key, never from the request body.',
    object: {
      name: 'The fee object',
      fields: [
        { name: 'id', type: 'string', description: 'Auto-generated ID for the fee.' },
        { name: 'name', type: 'string', description: 'Display name for the fee.' },
        { name: 'productId', type: 'string', description: 'ID of the product this fee belongs to.' },
        { name: 'type', type: 'enum', enumRef: 'FeeType', description: 'One of `FIXED` or `METERED`.' },
        { name: 'hsnSac', type: 'string | null', description: 'HSN/SAC code (up to 8 digits).' },
        { name: 'currencies', type: 'string[]', description: 'ISO currency codes this fee applies to.' },
        { name: 'currencyWiseTax', type: 'array<CurrencyTax>', description: 'Per-currency tax overrides.' },
        { name: 'currencyWiseBillLimit', type: 'array<CurrencyBillLimit>', description: 'Per-currency bill limits.' },
        { name: 'usageAttributes', type: 'array<FeeUsageAttribute>', description: 'Usage-attribute weights used to compute billable quantity on `METERED` fees. Each entry is `{ apiCode, weight, name }` — `name` is the usage attribute\'s display name, returned for convenience.' },
        { name: 'rateAttributes', type: 'array<FeeRateAttribute>', description: 'Rate attributes (pricing dimensions) in play for this fee, each `{ apiCode, name, groups }`. Derived from the product\'s rate attributes — not settable through the fee endpoints.' },
        { name: 'rateCards', type: 'array<RateCardGroup>', description: 'Rate cards attached to this fee, grouped by attribute combination. Managed by the [Rate Cards](api-reference/fee-ratecards) endpoints — not settable through the fee endpoints.' },
        { name: 'unit', type: 'string | null', description: 'Unit label for the fee.' },
        { name: 'productName', type: 'string | null', description: 'Display name of the parent product, denormalized onto the response.' },
        { name: 'rateConfiguration', type: 'enum | null', enumRef: 'RateConfiguration', description: 'The parent product\'s rate configuration, denormalized onto the response. One of `SINGLE` or `MULTIPLE`.' },
        { name: 'description', type: 'string | null', description: 'Free-text description.' },
        { name: 'createdAt', type: 'string | null', description: 'Creation timestamp.' },
        { name: 'updatedAt', type: 'string | null', description: 'Timestamp of the most recent update.' },
        { name: 'updatedBy', type: 'string | null', description: 'User ID of the last editor, or `null` if the fee has never been updated.' },
      ],
      example: FEE,
    },
    endpoints: [
      {
        id: 'create-fee',
        method: 'POST',
        path: '/billing/fee',
        title: 'Create a fee',
        description: 'Creates a fee under a product.\n\nTo copy an existing fee instead of building one from scratch, set `isClone` to `true` and pass the fee to copy in `sourceFeeId`.\n\n`companyId` and `createdBy` are server-managed — do not include them in the body.',
        requestBody: [
          { name: 'name', type: 'string', required: true, description: 'Display name for the fee.' },
          { name: 'productId', type: 'string', required: true, description: 'ID of the product this fee belongs to.' },
          { name: 'type', type: 'enum', required: true, enumRef: 'FeeType', description: 'One of `FIXED` or `METERED`.' },
          { name: 'isClone', type: 'boolean', required: true, description: 'Pass `false` for a new fee, or `true` to clone from `sourceFeeId`.' },
          { name: 'description', type: 'string', required: false, description: 'Free-text description. Defaults to `null`.' },
          { name: 'hsnSac', type: 'string', required: false, description: 'HSN/SAC code (up to 8 digits). Defaults to `null`.' },
          { name: 'currencies', type: 'string[]', required: false, description: 'ISO currency codes this fee applies to. Defaults to `[]`.' },
          {
            name: 'currencyWiseTax',
            type: 'array<CurrencyTax>',
            required: false,
            description: 'Per-currency tax overrides. Defaults to `[]`.',
            children: CURRENCY_TAX_FIELDS,
          },
          {
            name: 'currencyWiseBillLimit',
            type: 'array<CurrencyBillLimit>',
            required: false,
            description: 'Per-currency bill limits. Defaults to `[]`.',
            children: CURRENCY_BILL_LIMIT_FIELDS,
          },
          {
            name: 'usageAttributes',
            type: 'array<UsageAttribute>',
            required: false,
            description: 'Usage-attribute weights, used to compute billable quantity on `METERED` fees. Defaults to `[]`.',
            children: USAGE_ATTRIBUTE_FIELDS,
          },
          { name: 'parentFeeIds', type: 'string[]', required: false, description: 'IDs of parent fees when this fee is dependent. Sending a non-empty list marks the fee as a child fee. Defaults to `[]`.' },
          { name: 'unit', type: 'string', required: false, description: 'Unit label for the fee. Defaults to `null`.' },
          { name: 'sourceFeeId', type: 'string', required: false, description: 'ID of the fee to clone. Required when `isClone` is `true`, and must be omitted when `isClone` is `false` — sending it anyway is a `400`. Cloning copies the source fee\'s tag groups and rate cards onto the new fee.' },
        ],
        requestExample: {
          name: 'Platform Fee',
          productId: 'prod_abc123',
          type: 'FIXED',
          isClone: false,
          description: 'Monthly platform access charge',
          hsnSac: '998314',
          currencies: ['INR', 'USD'],
        },
        responseFields: [
          { name: 'data', type: 'Fee', description: 'The created fee.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Fee created successfully', FEE) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', 'sourceFeeId is required when isClone is true') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        extras: [
          {
            label: 'Clone',
            type: 'json',
            code:
`{
  "name": "Platform Fee - Copy",
  "productId": "prod_abc123",
  "type": "FIXED",
  "isClone": true,
  "sourceFeeId": "fee_xyz789"
}`,
          },
        ],
        errors: [
          { status: 400, when: 'A required field (`name`, `productId`, `type`, `isClone`) is missing or invalid.' },
          { status: 400, when: '`name` contains characters outside letters, digits, spaces, hyphens, underscores, and `& ( ) . ,`' },
          { status: 400, when: '`type` is not one of `FIXED` or `METERED`.' },
          { status: 400, when: '`isClone` is `true` but `sourceFeeId` is absent.' },
          { status: 400, when: '`isClone` is `false` but `sourceFeeId` is set.' },
          { status: 400, when: 'A `currencyWiseTax` entry sets both `taxRateId` and `taxGroupId`, or a currency appears twice.' },
          { status: 400, when: 'A `usageAttributes` entry is missing `apiCode`, or its `weight` is missing or not positive.' },
          { status: 400, when: 'A `usageAttributes[].apiCode` does not match any usage attribute on the product.' },
          { status: 400, when: 'No product exists with the given `productId` for your company.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'list-fees',
        method: 'POST',
        path: '/billing/fee/filter',
        title: 'List fees',
        description: 'Returns a paginated list of fees for your company. Pagination is required — both `pageNumber` and `pageSize` must be supplied.\n\nThis endpoint uses `POST` rather than `GET` so that filter conditions can be sent in the request body.',
        requestBody: paginationFields('EQUALS'),
        requestExample: {
          pageNumber: 1,
          pageSize: 20,
          orderBy: 'name',
          filterDicts: [{ field: 'name', op: 'EQUALS', value: 'Platform Fee' }],
        },
        responseFields: pageResponseFields('Fee', 'The fees on this page.'),
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Fetched fees successfully', FEE_LIST_PAGE) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: PAGINATION_ERRORS,
      },
      {
        id: 'update-fee',
        method: 'PUT',
        path: '/billing/fee/{fee_id}',
        title: 'Update a fee',
        description: `Updates a fee field by field.\n\n${UPDATES_DESCRIPTION}\n\nList-valued fields (\`currencies\`, \`currencyWiseTax\`, \`currencyWiseBillLimit\`, \`usageAttributes\`, \`parentFeeIds\`) are **full replacements**, not merges. Send the complete list you want to end up with.`,
        pathParams: [
          { name: 'fee_id', type: 'string', description: 'ID of the fee to update. Must belong to your company.' },
        ],
        requestBody: updatesBody([
          { name: 'name', type: 'string', required: false, description: 'New display name.' },
          { name: 'description', type: 'string', required: false, description: 'New description.' },
          { name: 'hsnSac', type: 'string', required: false, description: 'HSN/SAC code.' },
          { name: 'type', type: 'enum', required: false, enumRef: 'FeeType', description: 'One of `FIXED` or `METERED`.' },
          { name: 'currencies', type: 'string[]', required: false, description: 'Full replacement list of ISO currency codes.' },
          { name: 'currencyWiseTax', type: 'array<CurrencyTax>', required: false, description: 'Full replacement list of per-currency tax overrides.', children: CURRENCY_TAX_FIELDS },
          { name: 'currencyWiseBillLimit', type: 'array<CurrencyBillLimit>', required: false, description: 'Full replacement list of per-currency bill limits.', children: CURRENCY_BILL_LIMIT_FIELDS },
          { name: 'usageAttributes', type: 'array<UsageAttribute>', required: false, description: 'Full replacement list of usage-attribute weights.', children: USAGE_ATTRIBUTE_FIELDS },
          { name: 'parentFeeIds', type: 'string[]', required: false, description: 'Full replacement list of parent fee IDs.' },
          { name: 'unit', type: 'string', required: false, description: 'New unit label for the fee.' },
        ]),
        requestExample: {
          updates: {
            fields: ['name', 'description'],
            values: {
              name: 'Updated Fee Name',
              description: 'Revised description',
            },
          },
        },
        responseFields: [
          { name: 'data', type: 'Fee', description: 'The updated fee — same shape as the create response.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Updated fee successfully', { ...FEE, name: 'Updated Fee Name', updatedAt: '2026-06-14T09:12:00', updatedBy: 'usr_01xyz' }) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`updates` is missing, or `updates.fields` / `updates.values` is absent.' },
          UPDATES_MISMATCH_ERROR,
          { status: 400, when: '`name` is blank, or contains characters outside letters, digits, spaces, hyphens, underscores, and `& ( ) . ,`' },
          { status: 400, when: 'A `currencyWiseTax` entry sets both `taxRateId` and `taxGroupId`, or a currency appears twice.' },
          { status: 400, when: 'A `usageAttributes` entry is missing `apiCode`, or its `weight` is missing or not positive.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'delete-fee',
        method: 'DELETE',
        path: '/billing/fee/{fee_id}',
        title: 'Delete a fee',
        description: 'Deletes a fee. No request body is required.',
        pathParams: [
          { name: 'fee_id', type: 'string', description: 'ID of the fee to delete. Must belong to your company.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`DELETE /billing/fee/fee_abc123 HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data.id', type: 'string', description: 'ID of the deleted fee.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Fee deleted successfully', { id: 'fee_abc123' }) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
    ],
  },

  'fee-ratecards': {
    id: 'fee-ratecards',
    label: 'Rate Cards',
    tagline: 'Prices attached to a fee',
    description: 'A rate card holds the actual pricing for a fee: a billing scheme (per-unit, tiered, fixed, or min/max) plus the numbers that scheme needs. Rate cards are created under a fee and, when the fee\'s product has rate attributes, are scoped to a combination of attribute values.\n\n**Attributes and grouping.** You address a rate card by `attributes` — a map of rate attribute `apiName` to the values it covers, e.g. `{ "origin_region": ["North", "South"] }`. All values for one attribute must belong to the same group on that attribute; the server resolves them to the group name and stores it as the rate card\'s tag. The list endpoint returns rate cards grouped by that resolved combination, with the group\'s default surfaced separately.\n\n**Currency side effect.** Creating a rate card adds its `currency` to the parent fee\'s `currencies` list if it isn\'t already there.\n\n**Authentication.** Every endpoint requires the `X-Valyx-Signature` header. Session-cookie auth is **not** accepted on this namespace.',
    object: {
      name: 'The rate card object',
      fields: [
        { name: 'id', type: 'string', description: 'Auto-generated ID for the rate card.' },
        { name: 'name', type: 'string', description: 'Display name for the rate card.' },
        { name: 'feeComponentId', type: 'string', description: 'ID of the fee this rate card prices.' },
        { name: 'companyId', type: 'string', description: 'ID of the owning company.' },
        { name: 'currency', type: 'string', description: 'ISO currency code this rate card prices in.' },
        { name: 'isDefault', type: 'boolean', description: 'Whether this is the default rate card for its attribute combination on the fee.' },
        { name: 'description', type: 'object | null', description: 'The pricing strategy — `billingScheme` plus the fields that scheme requires. See the create request below for the per-scheme shape.' },
        { name: 'ratecardDetail', type: 'string | null', description: 'Human-readable rendering of the pricing, e.g. `Upto 100 kg - ₹ 15/kg`. Regenerated at read time from the current pricing and the fee\'s `unit`, so it never goes stale.' },
        { name: 'ratecardDescription', type: 'string | null', description: 'Free-text note about the rate card.' },
        { name: 'currencyId', type: 'string | null', description: 'Internal ID of the supported-currency record.' },
        { name: 'createdBy', type: 'string | null', description: 'User ID of the creator.' },
        { name: 'contractId', type: 'string | null', description: 'ID of the contract this rate card belongs to, or `null` for a template rate card available across all contracts.' },
      ],
      example: FEE_RATECARD,
    },
    endpoints: [
      {
        id: 'create-fee-ratecard',
        method: 'POST',
        path: '/billing/fee/{fee_id}/ratecard',
        title: 'Create a rate card',
        description: 'Adds a rate card to a fee.\n\n**Scheme and description work together.** Send the scheme as a top-level field; `description` carries only the numbers that scheme needs. The server merges them before validating, so a mismatch — tiers on a `FIXED_RATE` card, or a missing `perUnitPrice` on a `PER_UNIT` card — is a `400`.\n\n`attributes` maps each rate attribute\'s `apiName` to the values this rate card covers. Every value for a given attribute must belong to the same group on that attribute. Pass `{}` when the fee\'s product has no rate attributes.',
        pathParams: [
          { name: 'fee_id', type: 'string', description: 'ID of the fee to attach the rate card to.' },
        ],
        requestBody: [
          { name: 'name', type: 'string', required: true, description: 'Display name for the rate card.' },
          { name: 'billingScheme', type: 'enum', required: true, enumRef: 'BillingScheme', description: 'One of `PER_UNIT`, `VOLUME`, `GRADUATED`, `FIXED_RATE`, `MINMAX`. Determines which `description` fields are required.' },
          { name: 'currency', type: 'string', required: true, description: 'ISO currency code. Added to the parent fee\'s `currencies` list if not already present.' },
          { name: 'attributes', type: 'object<string, string[]>', required: true, description: 'Rate attribute `apiName` → the values this rate card covers, e.g. `{ "origin_region": ["North", "South"] }`. Each attribute\'s values must all belong to one group. Send `{}` when the product has no rate attributes.' },
          {
            name: 'description',
            type: 'object',
            required: true,
            description: 'The pricing numbers for the chosen `billingScheme`. Do not repeat `billingScheme` inside it — the server injects it.',
            children: PRICING_DESCRIPTION_FIELDS,
          },
          { name: 'ratecardDescription', type: 'string', required: false, description: 'Free-text note about the rate card.' },
          { name: 'contractId', type: 'string', required: false, description: 'Scope the rate card to a contract. Omit for a template rate card available across all contracts.' },
        ],
        requestExample: {
          name: 'Standard Freight Rates',
          billingScheme: 'GRADUATED',
          currency: 'INR',
          attributes: { origin_region: ['North', 'South'] },
          description: {
            isProrated: false,
            tiers: [
              { upTo: 100, unitPrice: 15, name: 'First 100 kg' },
              { upTo: 500, unitPrice: 10, name: 'Next 400 kg' },
              { upTo: 'inf', unitPrice: 8, name: 'Beyond 500 kg' },
            ],
          },
          ratecardDescription: 'Volume freight pricing for northern routes',
        },
        extras: [
          {
            label: 'Per-unit',
            type: 'json',
            code:
`{
  "name": "Flat Per-Kg Rate",
  "billingScheme": "PER_UNIT",
  "currency": "INR",
  "attributes": {},
  "description": { "perUnitPrice": 12.5 }
}`,
          },
          {
            label: 'Min/max',
            type: 'json',
            code:
`{
  "name": "Minimum Charge Rate",
  "billingScheme": "MINMAX",
  "currency": "INR",
  "attributes": {},
  "description": {
    "operationType": "MAXIMUM",
    "isPercentage": false,
    "perUsageValue": 12.5,
    "flatRate": 500
  }
}`,
          },
        ],
        responseFields: [
          { name: 'data', type: 'RateCard', description: 'The created rate card.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Ratecard created successfully', FEE_RATECARD) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', "Value 'Central' for attribute 'origin_region' does not belong to any tag group") },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: 'A required field (`name`, `billingScheme`, `currency`, `attributes`, `description`) is missing.' },
          { status: 400, when: '`billingScheme` is not one of `PER_UNIT`, `VOLUME`, `GRADUATED`, `FIXED_RATE`, `MINMAX`.' },
          { status: 400, when: 'An attribute in `attributes` has an empty value list.' },
          { status: 400, when: 'A value in `attributes` does not belong to any tag group on that rate attribute.' },
          { status: 400, when: 'Values for one attribute span more than one group.' },
          { status: 400, when: '`PER_UNIT` without `perUnitPrice`, or `FIXED_RATE` without `fixedPrice`.' },
          { status: 400, when: '`VOLUME` or `GRADUATED` with a missing or empty `tiers` list.' },
          { status: 400, when: '`tiers` supplied for `PER_UNIT`, `FIXED_RATE`, or `MINMAX`.' },
          { status: 400, when: 'A tier has `upTo` missing, ≤ 0, or not a whole number; or two tiers share the same `upTo`.' },
          { status: 400, when: 'A tier has `unitPrice` or `flatPrice` explicitly set to `null`.' },
          { status: 400, when: '`MINMAX` missing `operationType`, `isPercentage`, or `perUsageValue`; or missing both `flatRate` and `perCountValue`; or `perCountValue` without `isPercentageForCount`.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'list-fee-ratecards',
        method: 'POST',
        path: '/billing/fee/{fee_id}/ratecards',
        title: 'List rate cards',
        description: 'Returns the fee\'s rate cards for one currency, **grouped by attribute combination** and paginated over those groups (not over individual rate cards).\n\nThis endpoint uses `POST` rather than `GET` so that attribute filters can be sent in the request body. Its body differs from the other `filter` endpoints in this API: there is no `orderBy` or `filterDicts`, and `currency` is required.\n\nAn `attributeFilters` value that matches no tag group returns an **empty page** rather than a `400` — unlike create, which rejects it.',
        pathParams: [
          { name: 'fee_id', type: 'string', description: 'ID of the fee whose rate cards you want.' },
        ],
        requestBody: [
          { name: 'pageNumber', type: 'integer', required: true, description: '1-based page index over the attribute groups.' },
          { name: 'pageSize', type: 'integer', required: true, description: 'Number of groups per page.' },
          { name: 'currency', type: 'string', required: true, description: 'ISO currency code. Only rate cards in this currency are returned — there is no "all currencies" option.' },
          { name: 'attributeFilters', type: 'object<string, string[]>', required: false, description: 'Restrict results to rate cards carrying these attribute values, in the same shape as `attributes` on create. Keys whose value list is empty are dropped; if every key is dropped the filter is treated as absent.' },
          { name: 'contractId', type: 'string', required: false, description: 'Accepted by the request model but **not currently applied** — the route does not forward it to the query, so results are not filtered by contract.' },
        ],
        requestExample: {
          pageNumber: 1,
          pageSize: 20,
          currency: 'INR',
          attributeFilters: { origin_region: ['North'] },
        },
        responseFields: pageResponseFields('RateCardGroup', 'Attribute groups on this page. Each is `{ attributes, ratecards, defaultRatecard }` — `attributes` lists every value in the resolved group, `ratecards` holds the group\'s rate cards, and `defaultRatecard` is whichever one has `isDefault: true`, or `null`.'),
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Ratecards fetched successfully', FEE_RATECARD_GROUP_PAGE) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`pageNumber` is missing or not a positive integer.' },
          { status: 400, when: '`pageSize` is missing or not a positive integer.' },
          { status: 400, when: '`currency` is missing.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'update-fee-ratecard',
        method: 'PUT',
        path: '/billing/fee/ratecards/{ratecard_id}',
        title: 'Update a rate card',
        description: `Updates a rate card field by field. Only three fields are updatable: \`name\`, \`description\`, and \`ratecard_description\`. Naming anything else in \`updates.fields\` returns a \`400\` — currency, attributes, and default status all have their own routes or are fixed at creation.\n\n${UPDATES_DESCRIPTION}\n\n**Casing quirk:** \`updates.fields\` is checked against the updatable set **before** camelCase is converted, so the note field must be spelled \`ratecard_description\` there — \`ratecardDescription\` is rejected. Inside \`updates.values\` it is the opposite: use camelCase \`ratecardDescription\`. \`name\` and \`description\` are spelled the same either way.\n\nReplacing \`description\` replaces the whole pricing strategy and is re-validated in full, exactly as on create.`,
        pathParams: [
          { name: 'ratecard_id', type: 'string', description: 'ID of the rate card to update. Note this route is not nested under the fee.' },
        ],
        requestBody: updatesBody([
          { name: 'name', type: 'string', required: false, description: 'New display name. May not be blank.' },
          { name: 'description', type: 'object', required: false, description: 'Full replacement pricing strategy. Unlike create, `billingScheme` must be included **inside** this object — there is no sibling field to merge in. Re-validated in full.', children: PRICING_DESCRIPTION_FIELDS },
          { name: 'ratecardDescription', type: 'string', required: false, description: 'New free-text note. Spell it `ratecard_description` in `updates.fields` and `ratecardDescription` here.' },
        ]),
        requestExample: {
          updates: {
            fields: ['name', 'ratecard_description'],
            values: {
              name: 'Standard Freight Rates v2',
              ratecardDescription: 'Revised for the 2026 fuel surcharge',
            },
          },
        },
        responseFields: [
          { name: 'data', type: 'RateCard', description: 'The updated rate card.' },
        ],
        responseExamples: [
          {
            label: '200 OK',
            status: 200,
            body: envelope('Updated ratecard successfully', {
              ...FEE_RATECARD,
              name: 'Standard Freight Rates v2',
              ratecardDescription: 'Revised for the 2026 fuel surcharge',
            }),
          },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', 'Fields not updatable: currency') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`updates` is missing, or `updates.fields` / `updates.values` is absent.' },
          { status: 400, when: '`updates.fields` names anything outside `name`, `description`, `ratecard_description` — including the camelCase `ratecardDescription`.' },
          UPDATES_MISMATCH_ERROR,
          { status: 400, when: '`name` is provided but blank.' },
          { status: 400, when: 'A replacement `description` fails pricing-strategy validation (same rules as create).' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'delete-fee-ratecard',
        method: 'DELETE',
        path: '/billing/fee/ratecards/{ratecard_id}',
        title: 'Delete a rate card',
        description: 'Deletes a rate card. No request body is required.\n\nA rate card that is already attached to a contract cannot be deleted — detach it from the contract first.',
        pathParams: [
          { name: 'ratecard_id', type: 'string', description: 'ID of the rate card to delete.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`DELETE /billing/fee/ratecards/rc_abc123 HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data.id', type: 'string', description: 'ID of the deleted rate card.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Ratecard deleted successfully', { id: 'rc_abc123' }) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', 'Cannot delete a ratecard which is already associated to a contract') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: 'The rate card is associated with at least one contract.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'set-fee-ratecard-default',
        method: 'PUT',
        path: '/billing/fee/ratecards/{ratecard_id}/default',
        title: 'Set a rate card as default',
        description: 'Marks this rate card as the default for its fee. The previous default on the same fee is cleared in the same operation.\n\nNo request body is required.',
        pathParams: [
          { name: 'ratecard_id', type: 'string', description: 'ID of the rate card to promote to default.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`PUT /billing/fee/ratecards/rc_abc123/default HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data', type: 'RateCard', description: 'The rate card, now with `isDefault: true`. `contractId` is omitted from this particular response and always comes back `null`, even for a contract-scoped rate card — read it back via the list endpoint if you need it.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Ratecard set as default successfully', { ...FEE_RATECARD, isDefault: true, contractId: null }) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
    ],
  },

  contracts: {
    id: 'contracts',
    label: 'Contracts',
    tagline: 'What a customer has agreed to pay',
    description: 'A contract binds a customer to a set of fees for a period of time. It carries the commercial terms — period, addresses, GSTINs, discounts, and spend limits — while the per-fee billing configuration lives on the [Contract Fee Components](api-reference/contract-fee-components) attached to it.\n\n**Contracts are created as drafts.** `POST /billing/contract` rejects a contract whose `status` is `ACTIVE` or `SCHEDULED`, and the underlying model defaults to `ACTIVE` — so you must send `"status": "DRAFT"` explicitly. Attach fee components, then activate the contract with the update endpoint; activation fails if the contract has no fee components.\n\n**Authentication.** Every endpoint requires the `X-Valyx-Signature` header. `companyId` and `createdBy` come from the resolved API key, never from the request body.',
    object: {
      name: 'The contract object',
      fields: [
        { name: 'id', type: 'string | null', description: 'Auto-generated ID for the contract.' },
        { name: 'contractNumber', type: 'string | null', description: 'Your contract number. Must be unique within your company.' },
        { name: 'contractTitle', type: 'string | null', description: 'Human-readable title.' },
        { name: 'contractPeriod', type: 'TimeInterval | null', description: 'How long the contract runs, e.g. `{ "unit": "YEAR", "value": 1 }`.' },
        { name: 'status', type: 'enum | null', enumRef: 'ContractStatus', description: 'One of `DRAFT`, `SCHEDULED`, `ACTIVE`, `CANCELLED`, `EXPIRED`, `INACTIVE`.' },
        { name: 'customerId', type: 'string | null', description: 'ID of the customer being billed.' },
        { name: 'customerName', type: 'string | null', description: 'Resolved customer display name.' },
        { name: 'buyerId', type: 'string | null', description: 'Your own customer identifier, if the contract was created with `buyerId` instead of `customerId`.' },
        { name: 'startDate', type: 'string | null', description: 'Contract start date, `YYYY-MM-DD`.' },
        { name: 'endDate', type: 'string | null', description: 'Contract end date. Derived as `startDate + contractPeriod − 1 day` — not settable at creation.' },
        { name: 'nextBillingDate', type: 'string | null', description: 'Next scheduled billing date.' },
        { name: 'autoRenew', type: 'boolean | null', description: 'Whether the contract renews on expiry. Defaults to `false`.' },
        { name: 'currency', type: 'string | null', description: 'Three-letter ISO currency code.' },
        { name: 'creditTermId', type: 'string | null', description: 'Credit term reference.' },
        { name: 'poNumber', type: 'string | null', description: 'Purchase order number.' },
        { name: 'noteInvoice', type: 'string | null', description: 'Rich-text note shown on invoices.' },
        { name: 'noteContract', type: 'string | null', description: 'Rich-text note shown on the contract.' },
        { name: 'terms', type: 'string | null', description: 'Terms and conditions text.' },
        { name: 'billingAddress', type: 'Address | null', description: 'Billing address.' },
        { name: 'shippingAddress', type: 'Address | null', description: 'Shipping address.' },
        { name: 'gstin', type: 'string | null', description: 'Customer GSTIN. Its first two digits must match the `billingAddress` state\'s GST code.' },
        { name: 'companyGstin', type: 'string | null', description: 'Your own GSTIN for this contract.' },
        { name: 'placeOfSupply', type: 'object | null', description: 'Place of supply for tax purposes.' },
        { name: 'gstTreatment', type: 'string | null', description: 'GST treatment applied to the customer.' },
        { name: 'billingDiscounts', type: 'array<BillingDiscount> | null', description: 'Contract-level discounts. Each must have a unique `priority`, and `duration` must be `null`.' },
        { name: 'billingReportLimit', type: 'BillLimit | null', description: 'Cap and floor applied to each billing report.' },
        { name: 'contractLimit', type: 'BillLimit | null', description: 'Cap and floor applied across the whole contract lifetime.' },
        { name: 'isSplitConfigEnabled', type: 'boolean | null', description: 'Whether invoice splitting is enabled. Defaults to `false`.' },
        { name: 'splitConfigMeta', type: 'object | null', description: 'Resolved split configuration and label mappings.' },
        { name: 'comments', type: 'array<Comment>', description: 'Comments recorded against the contract.' },
        { name: 'customFields', type: 'object | null', description: 'Custom document-property values, keyed by `propertyKey`. Only populated on the filter endpoint when `includeCustomFields=true`. See [Custom Fields](api-reference/custom-fields).' },
        { name: 'companyId', type: 'string | null', description: 'Owning company ID.' },
        { name: 'createdBy', type: 'string | null', description: 'User ID of the creator.' },
        { name: 'updatedBy', type: 'string | null', description: 'User ID of the last editor.' },
      ],
      example: CONTRACT,
    },
    endpoints: [
      {
        id: 'create-contract',
        method: 'POST',
        path: '/billing/contract',
        title: 'Create a contract',
        description: 'Creates a contract in `DRAFT`.\n\n**You must send** `"status": "DRAFT"`. The model defaults `status` to `ACTIVE`, and validation rejects `ACTIVE` and `SCHEDULED` with "A contract must be created as a draft. Add fee components and then activate it." Omitting `status` therefore fails.\n\n**Billing frequency is not a contract field.** It is configured per fee component when you attach one — see [Contract Fee Components](api-reference/contract-fee-components). Anything you send here under `billingFrequency` is silently discarded.\n\n`endDate` is derived from `startDate` + `contractPeriod`; `companyId` and `createdBy` come from the API key.',
        requestBody: [
          { name: 'contractNumber', type: 'string', required: true, description: 'Your contract number. Must be unique within your company — a duplicate is a `400`.' },
          { name: 'contractTitle', type: 'string', required: true, description: 'Human-readable title.' },
          { name: 'contractPeriod', type: 'TimeInterval', required: true, description: 'How long the contract runs. `value` must be an integer greater than 0.', children: TIME_INTERVAL_FIELDS },
          { name: 'status', type: 'enum', required: true, enumRef: 'ContractStatus', description: 'Must be `DRAFT`. Not technically required by the schema, but the default (`ACTIVE`) is rejected, so every successful create sends it.' },
          { name: 'customerId', type: 'string', required: false, description: 'ID of the customer to bill. Provide this **or** `buyerId` — a contract with neither is a `400`.' },
          { name: 'buyerId', type: 'string', required: false, description: 'Your own customer identifier. Provide this **or** `customerId`.' },
          { name: 'startDate', type: 'string', required: false, description: 'Contract start date, `YYYY-MM-DD`. `endDate` is derived from it and `contractPeriod`.' },
          { name: 'currency', type: 'string', required: false, description: 'Three-letter ISO currency code.' },
          { name: 'currencyId', type: 'string', required: false, description: 'Internal currency reference ID.' },
          { name: 'creditTermId', type: 'string', required: false, description: 'Credit term reference.' },
          { name: 'poNumber', type: 'string', required: false, description: 'Purchase order number.' },
          { name: 'noteInvoice', type: 'string', required: false, description: 'Rich-text note shown on invoices.' },
          { name: 'noteContract', type: 'string', required: false, description: 'Rich-text note shown on the contract.' },
          { name: 'terms', type: 'string', required: false, description: 'Terms and conditions text.' },
          { name: 'autoRenew', type: 'boolean', required: false, description: 'Renew the contract on expiry. Defaults to `false`.' },
          { name: 'renewalPeriod', type: 'TimeInterval', required: false, description: 'Period to renew for. Defaults to `contractPeriod`.', children: TIME_INTERVAL_FIELDS },
          { name: 'billingAddress', type: 'Address', required: false, description: 'Billing address. Its `state` is checked against `gstin`.', children: CONTRACT_ADDRESS_FIELDS },
          { name: 'shippingAddress', type: 'Address', required: false, description: 'Shipping address.', children: CONTRACT_ADDRESS_FIELDS },
          { name: 'gstin', type: 'string', required: false, description: 'Customer GSTIN. Its first two digits must match the `billingAddress` state\'s GST code.' },
          { name: 'companyGstin', type: 'string', required: false, description: 'Your own GSTIN for this contract.' },
          { name: 'placeOfSupply', type: 'object', required: false, description: 'Place of supply for tax purposes. Validated when supplied.' },
          { name: 'gstTreatment', type: 'string', required: false, description: 'GST treatment for the customer.' },
          { name: 'billingDiscounts', type: 'array<BillingDiscount>', required: false, description: 'Contract-level discounts. Defaults to `[]`. Every entry must have a distinct `priority`, and `duration` **must be** `null` — a contract-level discount applies for the whole contract.', children: BILLING_DISCOUNT_FIELDS },
          { name: 'billingReportLimit', type: 'BillLimit', required: false, description: 'Cap and floor applied to each billing report.', children: BILL_LIMIT_FIELDS },
          { name: 'contractLimit', type: 'BillLimit', required: false, description: 'Cap and floor applied across the contract lifetime.', children: BILL_LIMIT_FIELDS },
          { name: 'invoiceSplitConfig', type: 'object', required: false, description: 'Invoice split configuration. Validated when supplied.' },
          { name: 'splitConfig', type: 'object', required: false, description: 'Split group configuration.' },
          { name: 'isSplitConfigEnabled', type: 'boolean', required: false, description: 'Enable invoice splitting. Defaults to `false`.' },
          { name: 'customFields', type: 'object', required: false, description: 'Custom document-property values keyed by `propertyKey`. Every key must already be defined for `documentType = CONTRACT` — an unknown key fails the request before the contract is created. See [Custom Fields](api-reference/custom-fields).' },
        ],
        requestExample: {
          contractNumber: 'CONTRACT-001',
          contractTitle: 'Annual Logistics Agreement',
          contractPeriod: { unit: 'YEAR', value: 1 },
          status: 'DRAFT',
          customerId: 'cust_29_CUST-1042',
          startDate: '2026-01-01',
          currency: 'INR',
          gstin: '29ABCDE1234F1Z5',
          billingAddress: {
            line1: '221B Baker Street',
            city: 'Bengaluru',
            state: 'Karnataka',
            country: 'India',
            zipCode: '560001',
            stateCode: '29',
          },
        },
        responseFields: [
          { name: 'data.id', type: 'string', description: 'ID of the created contract. This endpoint returns only the ID — fetch the contract to read it back.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Added contract successfully', { id: 'contract_abc123' }) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', 'A contract must be created as a draft. Add fee components and then activate it.') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: 'The request body is empty, or `contractNumber` / `contractTitle` / `contractPeriod` is missing.' },
          { status: 400, when: '`status` is `ACTIVE` or `SCHEDULED` — including when it is omitted and defaults to `ACTIVE`.' },
          { status: 400, when: 'Another contract already exists with the same `contractNumber`.' },
          { status: 400, when: 'Neither `customerId` nor `buyerId` is supplied.' },
          { status: 400, when: '`contractPeriod.value` is not an integer greater than 0.' },
          { status: 400, when: 'Two `billingDiscounts` share the same `priority`, or any entry sets a non-null `duration`.' },
          { status: 400, when: 'A discount `percentage` is outside 0–100, or `priority` is not greater than 0.' },
          { status: 400, when: 'The first two digits of `gstin` do not match the `billingAddress` state\'s GST code.' },
          { status: 400, when: 'A `customFields` key is not a defined contract document property for your company.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'fetch-contract',
        method: 'GET',
        path: '/billing/contract/fetch/{contract_id}',
        title: 'Fetch a contract',
        description: 'Returns a single contract. No request body is required.\n\nNote this returns the commercial terms only — billing frequency, usage cycles, and prepaid settings live on the fee components, which you fetch separately.',
        pathParams: [
          { name: 'contract_id', type: 'string', description: 'ID of the contract to fetch.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`GET /billing/contract/fetch/contract_abc123 HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data', type: 'Contract', description: 'The requested contract. `customFields` is `null` here — only the filter endpoint populates it.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Fetched contract successfully', CONTRACT) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'filter-contracts',
        method: 'GET',
        path: '/billing/contract/filter',
        title: 'List contracts',
        description: 'Returns a paginated list of contracts. Unlike the `filter` endpoints elsewhere in this API, this one is a `GET` **with query parameters** — there is no request body and no `filterDicts`.\n\nAll filters combine with AND. `pageNumber` and `pageSize` are required.',
        queryParams: [
          { name: 'pageNumber', type: 'integer', required: true, description: '1-based page index.' },
          { name: 'pageSize', type: 'integer', required: true, description: 'Number of records per page.' },
          { name: 'customerId', type: 'string', required: false, description: 'Only contracts for this customer.' },
          { name: 'buyerId', type: 'string', required: false, description: 'Only contracts for this buyer identifier.' },
          { name: 'contractNumber', type: 'string', required: false, description: 'Only contracts with this contract number.' },
          { name: 'status', type: 'enum', required: false, enumRef: 'ContractStatus', description: 'Only contracts in this status. One of `DRAFT`, `SCHEDULED`, `ACTIVE`, `CANCELLED`, `EXPIRED`, `INACTIVE`.' },
          { name: 'contractDurationValue', type: 'integer', required: false, description: 'Numeric part of the contract duration — `3` for a 3-year contract. Pair with `contractDurationUnit`.' },
          { name: 'contractDurationUnit', type: 'enum', required: false, enumRef: 'TimeUnit', description: 'Unit part of the contract duration — `YEAR` for a 3-year contract.' },
          { name: 'billingFrequencyValue', type: 'integer', required: false, description: 'Numeric part of the billing frequency — `2` for every 2 months.' },
          { name: 'billingFrequencyUnit', type: 'enum', required: false, enumRef: 'TimeUnit', description: 'Unit part of the billing frequency — `MONTH` for every 2 months.' },
          { name: 'nextBillingDateStart', type: 'string', required: false, description: 'Only contracts whose next billing date is on or after this `YYYY-MM-DD` date.' },
          { name: 'nextBillingDateEnd', type: 'string', required: false, description: 'Only contracts whose next billing date is on or before this `YYYY-MM-DD` date.' },
          { name: 'currencies', type: 'string', required: false, description: 'Currency code to filter by. Repeatable — send `currencies=INR&currencies=USD` to match either.' },
          { name: 'includeCustomFields', type: 'boolean', required: false, description: 'When `true`, each contract carries its custom document-property values in `customFields`. Defaults to `false`.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`GET /billing/contract/filter?pageNumber=1&pageSize=20&status=ACTIVE&currencies=INR&includeCustomFields=true HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: pageResponseFields('Contract', 'The contracts on this page.'),
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Fetched contracts successfully', CONTRACT_LIST_PAGE) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`pageNumber` or `pageSize` is missing or not an integer.' },
          { status: 400, when: '`status` is not one of the `ContractStatus` values.' },
          { status: 400, when: '`nextBillingDateStart` is not a valid `YYYY-MM-DD` date.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'update-contract',
        method: 'POST',
        path: '/billing/contract/update/{contract_id}',
        title: 'Update a contract',
        description: 'Updates a contract field by field, and is also how you **activate** a draft: set `status` to `ACTIVE` once at least one fee component is attached. A future `startDate` produces `SCHEDULED` instead of `ACTIVE`, and `endDate` is re-derived whenever `startDate` or `contractPeriod` changes.\n\n`updates.fields` **is currently ignored on this endpoint.** The handler reads the field list from `updates.values`, so the fields actually written are the **keys present in** `updates.values`. Send `fields` anyway for forward compatibility, but keep it in sync with `values` — `values` is what takes effect.\n\nWhile a contract is `ACTIVE`, `startDate`, `endDate`, `billingFrequency`, and `usageFrequency` are immutable.',
        pathParams: [
          { name: 'contract_id', type: 'string', description: 'ID of the contract to update.' },
        ],
        requestBody: updatesBody([
          { name: 'status', type: 'enum', required: false, enumRef: 'ContractStatus', description: 'Set to `ACTIVE` to activate the contract. Requires at least one attached fee component; a future `startDate` results in `SCHEDULED` instead.' },
          { name: 'customerId', type: 'string', required: false, description: 'Reassign the contract to another customer.' },
          { name: 'contractNumber', type: 'string', required: false, description: 'New contract number.' },
          { name: 'contractTitle', type: 'string', required: false, description: 'New title.' },
          { name: 'contractPeriod', type: 'TimeInterval', required: false, description: 'New contract duration. Re-derives `endDate`.', children: TIME_INTERVAL_FIELDS },
          { name: 'renewalPeriod', type: 'TimeInterval', required: false, description: 'Period used when auto-renewing.', children: TIME_INTERVAL_FIELDS },
          { name: 'usageBillingCycle', type: 'TimeInterval', required: false, description: 'Contract-level usage billing cycle. Settable here but not at creation.', children: TIME_INTERVAL_FIELDS },
          { name: 'startDate', type: 'string', required: false, description: 'New start date, `YYYY-MM-DD`. Re-derives `endDate`. Immutable while the contract is `ACTIVE`.' },
          { name: 'endDate', type: 'string', required: false, description: 'New end date. Normally derived — immutable while the contract is `ACTIVE`.' },
          { name: 'cancellationDate', type: 'string', required: false, description: 'Date the contract was cancelled, `YYYY-MM-DD`.' },
          { name: 'nextBillingDate', type: 'string', required: false, description: 'Override the next billing date, `YYYY-MM-DD`.' },
          { name: 'creditTermId', type: 'string', required: false, description: 'Credit term reference.' },
          { name: 'poNumber', type: 'string', required: false, description: 'Purchase order number.' },
          { name: 'noteInvoice', type: 'string', required: false, description: 'Rich-text note shown on invoices.' },
          { name: 'noteContract', type: 'string', required: false, description: 'Rich-text note shown on the contract.' },
          { name: 'terms', type: 'string', required: false, description: 'Terms and conditions text.' },
          { name: 'autoRenew', type: 'boolean', required: false, description: 'Whether the contract renews on expiry.' },
          { name: 'billingAddress', type: 'Address', required: false, description: 'New billing address. Re-checked against `gstin`.', children: CONTRACT_ADDRESS_FIELDS },
          { name: 'shippingAddress', type: 'Address', required: false, description: 'New shipping address.', children: CONTRACT_ADDRESS_FIELDS },
          { name: 'gstin', type: 'string', required: false, description: 'Customer GSTIN. First two digits must match the billing address state\'s GST code.' },
          { name: 'companyGstin', type: 'string', required: false, description: 'Your own GSTIN.' },
          { name: 'placeOfSupply', type: 'object', required: false, description: 'Place of supply for tax purposes.' },
          { name: 'gstTreatment', type: 'string', required: false, description: 'GST treatment for the customer.' },
          { name: 'billingDiscounts', type: 'array<BillingDiscount>', required: false, description: 'Full replacement list of contract-level discounts. `duration` must be `null` on every entry.', children: BILLING_DISCOUNT_FIELDS },
          { name: 'billingReportLimit', type: 'BillLimit', required: false, description: 'Cap and floor per billing report.', children: BILL_LIMIT_FIELDS },
          { name: 'contractLimit', type: 'BillLimit', required: false, description: 'Cap and floor across the contract lifetime.', children: BILL_LIMIT_FIELDS },
          { name: 'invoiceSplitConfig', type: 'object', required: false, description: 'Invoice split configuration.' },
          { name: 'splitConfig', type: 'object', required: false, description: 'Split group configuration.' },
          { name: 'isSplitConfigEnabled', type: 'boolean', required: false, description: 'Enable or disable invoice splitting.' },
          { name: 'usageAggregationsLastUpdated', type: 'array', required: false, description: 'Usage-aggregation watermarks.' },
        ]),
        requestExample: {
          updates: {
            fields: ['contractTitle', 'autoRenew'],
            values: {
              contractTitle: 'Annual Logistics Agreement (rev 2)',
              autoRenew: true,
            },
          },
        },
        responseFields: [
          { name: 'data.id', type: 'string', description: 'ID of the updated contract. The handler builds the full contract but the response is marshalled down to the ID.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Updated contract successfully', { id: 'contract_abc123' }) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', 'Cannot activate or schedule a contract without any fee components. Please add at least one fee component before activating or scheduling the contract.') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`updates` or `updates.values` is absent.' },
          { status: 400, when: '`status` is set to `ACTIVE`/`SCHEDULED` but the contract has no fee components.' },
          { status: 400, when: '`startDate`, `endDate`, `billingFrequency`, or `usageFrequency` is changed while the contract is `ACTIVE`.' },
          { status: 400, when: 'A `billingDiscounts` entry sets a non-null `duration`.' },
          { status: 400, when: 'The first two digits of `gstin` do not match the billing address state\'s GST code.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'cancel-contract',
        method: 'POST',
        path: '/billing/contract/cancel/{contract_id}',
        title: 'Cancel a contract',
        description: 'Cancels a contract. No request body is required.\n\nNote the response shape: `data` is the contract ID as a **bare string**, not an object — unlike every other endpoint in this namespace, which wraps it as `{ "id": ... }`.',
        pathParams: [
          { name: 'contract_id', type: 'string', description: 'ID of the contract to cancel.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`POST /billing/contract/cancel/contract_abc123 HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data', type: 'string', description: 'ID of the cancelled contract, as a plain string.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Cancelled contract successfully', 'contract_abc123') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
    ],
  },

  'contract-fee-components': {
    id: 'contract-fee-components',
    label: 'Contract Fee Components',
    tagline: 'Fees attached to a contract',
    description: 'Attaching a [Fee](api-reference/fees) to a [Contract](api-reference/contracts) creates a **contract fee component** — the relationship that carries all the per-fee billing configuration: billing frequency, usage cycle, taxes, bill limits, discounts, and prepaid settings.\n\nThis is where billing frequency lives. The contract itself has no `billingFrequency` field; each attached fee has its own, and it is required at attach time.\n\n**Authentication.** Every endpoint requires the `X-Valyx-Signature` header. `companyId` and the acting user come from the resolved API key.',
    object: {
      name: 'The contract fee component object',
      fields: [
        { name: 'id', type: 'string | null', description: 'ID of the fee-component-to-contract relationship (not the fee ID).' },
        { name: 'productId', type: 'string | null', description: 'Product the underlying fee belongs to.' },
        { name: 'productName', type: 'string | null', description: 'Display name of that product.' },
        { name: 'sku', type: 'string | null', description: 'SKU of the underlying product.' },
        { name: 'name', type: 'string | null', description: 'Fee name.' },
        { name: 'description', type: 'string | null', description: 'Fee description.' },
        { name: 'hsnOrSacCode', type: 'string | null', description: 'HSN/SAC tax code.' },
        { name: 'unit', type: 'string | null', description: 'Unit of the fee, derived from its usage attributes.' },
        { name: 'isFlatFee', type: 'boolean | null', description: 'Whether this is a flat, non-usage-based fee. Defaults to `false`.' },
        { name: 'isPreusage', type: 'boolean | null', description: 'Whether the fee is prepaid / estimate-based. Defaults to `false`.' },
        { name: 'includedInUsage', type: 'boolean | null', description: 'Whether this fee\'s usage feeds contract-level usage totals.' },
        { name: 'usage', type: 'number | null', description: 'Actual usage for the applicable billing period.' },
        { name: 'billedAmount', type: 'number | null', description: 'Pre-tax amount billed for that actual usage.' },
        { name: 'estimatedUsage', type: 'number | null', description: 'Estimated usage. Only populated for prepaid fees.' },
        { name: 'estimatedBilledAmount', type: 'number | null', description: 'Pre-tax amount billed for the estimated usage.' },
        { name: 'adjustment', type: 'number | null', description: '`billedAmount − estimatedBilledAmount`, carried into the next billing report as an adjustment.' },
        { name: 'taxRate', type: 'array<FeeComponentTax> | null', description: 'Taxes applied to this fee component.' },
        { name: 'billLimit', type: 'BillLimit | null', description: 'Cap and floor for this fee component.' },
        { name: 'discounts', type: 'array<BillingDiscount> | null', description: 'Discounts on this relationship. Note the field is plural — `discounts`.' },
        { name: 'activePeriod', type: 'FeeComponentPeriod | null', description: 'Window during which the fee is active.' },
        { name: 'billingFrequency', type: 'TimeInterval | null', description: 'How often this fee is billed.' },
        { name: 'usageCycle', type: 'TimeInterval | null', description: 'How often usage is aggregated.' },
        { name: 'estimateCycle', type: 'TimeInterval | null', description: 'How often usage estimates are generated.' },
        { name: 'virtualStartDate', type: 'string | null', description: 'Anchor date used to compute billing periods.' },
        { name: 'firstBillingPeriodEndDate', type: 'string | null', description: 'End of the first billing period. Derived as `virtualStartDate + billingFrequency − 1 day` when not set explicitly.' },
        { name: 'applicableBillingPeriod', type: 'Period | null', description: 'The billing period the `usage` and `billedAmount` figures were computed over.' },
        { name: 'ratecards', type: 'array<ContractRateCard> | null', description: 'Rate cards applicable to this fee on this contract. Each may nest `timedRatecards` of the same shape.' },
        { name: 'advanceConfig', type: 'AdvanceConfig | null', description: 'True-up / true-down configuration for prepaid billing.' },
        { name: 'estimates', type: 'object | null', description: 'Usage-estimate table info — tag combinations, usage attributes, and any estimates already entered. `null` when the fee is not prepaid.' },
        { name: 'estimateCycleWiseSummary', type: 'array | null', description: 'Per-estimate-cycle actual/estimated/adjustment breakdown. Populated for prepaid fees.' },
        { name: 'lastRefreshed', type: 'string | null', description: 'When this fee component\'s usage and billed figures were last recalculated. Per-fee-component — there is no top-level `lastRefreshed`.' },
        { name: 'companyId', type: 'string | null', description: 'Owning company ID.' },
        { name: 'createdBy', type: 'string | null', description: 'User ID that created the relationship.' },
        { name: 'updatedBy', type: 'string | null', description: 'User ID that last updated the relationship.' },
      ],
      example: CONTRACT_FEE_COMPONENT,
    },
    endpoints: [
      {
        id: 'attach-contract-fee-component',
        method: 'POST',
        path: '/billing/contract/feeComponent',
        title: 'Attach a fee to a contract',
        description: 'Attaches an existing fee to a contract, creating the relationship that holds its billing configuration. Default rate cards are selected automatically.\n\n`additionalParams.billingFrequency` **is required.** The model types `additionalParams` as optional, but the service rejects the call when it — or the billing frequency inside it — is missing.\n\nThe path is `/billing/contract/feeComponent`; there is no `/attach` suffix.',
        requestBody: [
          { name: 'contractId', type: 'string', required: true, description: 'ID of the contract to attach the fee to.' },
          { name: 'feeComponentId', type: 'string', required: true, description: 'ID of the fee to attach.' },
          { name: 'additionalParams', type: 'object', required: true, description: 'Initial billing configuration for the relationship. Effectively required — `billingFrequency` inside it must be set. Same shape as `updates.values` on the update endpoint.', children: CONTRACT_FEE_COMPONENT_FIELDS },
        ],
        requestExample: {
          contractId: 'contract_abc123',
          feeComponentId: 'fee_abc123',
          additionalParams: {
            billingFrequency: { unit: 'MONTH', value: 1 },
            activePeriod: { startDate: '2026-01-01', endDate: null },
            includedInUsage: true,
            isPreusage: false,
          },
        },
        responseFields: [
          { name: 'data.id', type: 'string', description: 'ID of the fee that was attached (echoed from the request, not the new relationship ID).' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Added fee component for contract successfully', { id: 'fee_abc123' }) },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', 'billing_frequency must be provided when adding a fee component') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`contractId` or `feeComponentId` is missing.' },
          { status: 400, when: '`additionalParams` is absent, or `additionalParams.billingFrequency` is not set.' },
          { status: 400, when: 'A `billLimit` or `usageDriverWeights` entry fails validation — overlapping weight periods, or a tier with neither `minValue` nor `maxValue`.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'fetch-contract-fee-components',
        method: 'GET',
        path: '/billing/contract/feeComponent/{contract_id}',
        title: 'Fetch a contract\'s fee components',
        description: 'Returns every fee component attached to a contract, with usage and billed amounts for each one\'s most recent billing cycle.\n\n`data` is a **flat array** — it is not wrapped in a `feeComponents` key. No request body is required.',
        pathParams: [
          { name: 'contract_id', type: 'string', description: 'ID of the contract whose fee components you want.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`GET /billing/contract/feeComponent/contract_abc123 HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data', type: 'array<ContractFeeComponent>', description: 'Every fee component attached to the contract.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Fetched fee components for contract successfully', [CONTRACT_FEE_COMPONENT]) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'update-contract-fee-component',
        method: 'POST',
        path: '/billing/contract/{contract_id}/feeComponent/{fee_component_id}/update',
        title: 'Update a fee on a contract',
        description: 'Updates the billing configuration of one fee on one contract, field by field.\n\nUnlike the contract update endpoint, this one reads `updates.fields` correctly — only the fields you name there are written, and names are converted from camelCase server-side.\n\nList-valued fields (`taxRate`, `discounts`, `usageDriverWeights`) are full replacements.',
        pathParams: [
          { name: 'contract_id', type: 'string', description: 'ID of the contract.' },
          { name: 'fee_component_id', type: 'string', description: 'ID of the fee whose relationship you are updating.' },
        ],
        requestBody: updatesBody(CONTRACT_FEE_COMPONENT_FIELDS),
        requestExample: {
          updates: {
            fields: ['billingFrequency', 'billLimit', 'taxRate'],
            values: {
              billingFrequency: { unit: 'MONTH', value: 1 },
              billLimit: { minValue: 100.0, maxValue: 5000.0 },
              taxRate: [{ name: 'IGST', value: 18.0, isActive: true }],
            },
          },
        },
        responseFields: [
          { name: 'data', type: 'ContractFeeComponent', description: 'The updated fee component, in full — the same shape returned by the fetch endpoint.' },
        ],
        responseExamples: [
          {
            label: '200 OK',
            status: 200,
            body: envelope('Updated fee component for contract successfully', {
              ...CONTRACT_FEE_COMPONENT,
              billLimit: { isUsageBased: false, minValue: 100.0, maxValue: 5000.0, isProrated: null, isActive: true, tiers: null },
            }),
          },
          { label: '400 Bad Request', status: 400, body: errorEnvelope('ValueError', 'Billing type of a fee cannot be edited') },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`updates` is missing, or `updates.fields` / `updates.values` is absent.' },
          { status: 400, when: '`isPreusage` is changed while the contract is `ACTIVE` (`Billing type of a fee cannot be edited`).' },
          { status: 400, when: 'A `billLimit` tier sets neither `minValue` nor `maxValue`, or `isUsageBased` is `true` with an empty `tiers` list.' },
          { status: 400, when: 'Two `usageDriverWeights` periods overlap, or a weight entry has an empty period list.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'include-usage-contract-fee-components',
        method: 'POST',
        path: '/billing/contract/feeComponent/includeUsage',
        title: 'Include fees in usage calculation',
        description: 'Marks a set of fees on a contract as contributing to contract-level usage totals — the figures that drive min/max bill limits and usage-based discount tiers.\n\nThis is the bulk equivalent of setting `includedInUsage` on each fee individually. The response carries no payload: `data` is an empty object.',
        requestBody: [
          { name: 'contractId', type: 'string', required: true, description: 'ID of the contract.' },
          { name: 'feeComponentIds', type: 'string[]', required: true, description: 'IDs of the fees to include in contract-level usage calculation.' },
        ],
        requestExample: {
          contractId: 'contract_abc123',
          feeComponentIds: ['fee_abc123', 'fee_def456'],
        },
        responseFields: [
          { name: 'data', type: 'object', description: 'Always an empty object — the endpoint returns no payload.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Added fee components for usage calculation', {}) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`contractId` or `feeComponentIds` is missing.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'delete-contract-fee-component',
        method: 'DELETE',
        path: '/billing/contract/feeComponent/delete',
        title: 'Detach a fee from a contract',
        description: 'Removes a fee from a contract. Both identifiers go in the **request body**, not the path — this is a `DELETE` that carries a body.',
        requestBody: [
          { name: 'contractId', type: 'string', required: true, description: 'ID of the contract.' },
          { name: 'feeComponentId', type: 'string', required: true, description: 'ID of the fee to detach.' },
        ],
        requestExample: {
          contractId: 'contract_abc123',
          feeComponentId: 'fee_abc123',
        },
        responseFields: [
          { name: 'data.id', type: 'string', description: 'ID of the fee that was detached.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Deleted fee component for contract successfully', { id: 'fee_abc123' }) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`contractId` or `feeComponentId` is missing.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'upsert-contract-ratecards',
        method: 'POST',
        path: '/billing/contract/feeComponent/ratecard/{contract_id}',
        title: 'Set rate cards for a fee on a contract',
        description: 'Creates or replaces the rate-card-to-tag-combination mappings for one fee on a contract. This is how you override, per contract, which [Rate Card](api-reference/fee-ratecards) applies to each attribute combination.',
        pathParams: [
          { name: 'contract_id', type: 'string', description: 'ID of the contract.' },
        ],
        requestBody: [
          { name: 'feeComponentId', type: 'string', required: true, description: 'ID of the fee whose rate cards you are mapping.' },
          {
            name: 'ratecardRelations',
            type: 'array<RatecardRelation>',
            required: true,
            description: 'The mappings to apply. Replaces the existing set for this fee on this contract.',
            children: [
              { name: 'ratecardId', type: 'string | null', required: false, description: 'Rate card to apply for this tag combination.' },
              { name: 'tags', type: 'object<string, string>', required: false, description: 'Resolved tag combination this mapping covers, e.g. `{ "origin_region": "North-South" }`.' },
            ],
          },
        ],
        requestExample: {
          feeComponentId: 'fee_abc123',
          ratecardRelations: [
            { ratecardId: 'rc_abc123', tags: { origin_region: 'North-South' } },
            { ratecardId: 'rc_def456', tags: { origin_region: 'East-West' } },
          ],
        },
        responseFields: [
          { name: 'data.id', type: 'string', description: 'ID of the contract whose rate card mappings were updated — not the fee or rate card ID.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Added Ratecard for contract successfully', { id: 'contract_abc123' }) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`feeComponentId` or `ratecardRelations` is missing.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'fetch-contract-ratecards',
        method: 'GET',
        path: '/billing/contract/feeComponent/ratecard',
        title: 'Fetch rate card mappings',
        description: 'Returns the rate-card-to-tag-combination mappings on a contract, optionally narrowed to one fee. Both identifiers are query parameters — the contract ID is **not** in the path here.',
        queryParams: [
          { name: 'contractId', type: 'string', required: true, description: 'ID of the contract.' },
          { name: 'feeComponentId', type: 'string', required: false, description: 'Narrow the result to a single fee.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`GET /billing/contract/feeComponent/ratecard?contractId=contract_abc123&feeComponentId=fee_abc123 HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data', type: 'array<ContractRateCardRelation>', description: 'The mappings, each linking a tag combination to a rate card.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Fetched Ratecards for contract successfully', [CONTRACT_RATECARD_RELATION]) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`contractId` is missing.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
      {
        id: 'fetch-contract-usage-estimate',
        method: 'GET',
        path: '/billing/contract/usageEstimate',
        title: 'Fetch the usage estimate table',
        description: 'Returns the usage-estimate table for a contract over one usage cycle, with billed-amount rows. Used to render or export the estimate grid for prepaid fees.\n\nThe response is a generic table structure — columns plus rows — rather than a fixed object shape.',
        queryParams: [
          { name: 'contractId', type: 'string', required: true, description: 'ID of the contract.' },
          { name: 'usageCycleStartDate', type: 'string', required: true, description: 'Start of the usage cycle, `YYYY-MM-DD`.' },
          { name: 'usageCycleEndDate', type: 'string', required: true, description: 'End of the usage cycle, `YYYY-MM-DD`.' },
          { name: 'feeComponentId', type: 'string', required: false, description: 'Narrow the table to a single fee.' },
        ],
        extras: [
          {
            label: 'Example',
            type: 'http',
            code:
`GET /billing/contract/usageEstimate?contractId=contract_abc123&usageCycleStartDate=2026-01-01&usageCycleEndDate=2026-01-31 HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: <your-api-key>`,
          },
        ],
        responseFields: [
          { name: 'data', type: 'GenericTable', description: 'Table of usage estimates with billed-amount rows for the requested cycle.' },
        ],
        responseExamples: [
          { label: '200 OK', status: 200, body: envelope('Fetched usage estimate table for contract successfully', { columns: [], rows: [] }) },
          { label: '403 Forbidden', status: 403, body: errorEnvelope('ForbiddenError', 'Authentication required. Provide a valid API key.') },
        ],
        errors: [
          { status: 400, when: '`contractId`, `usageCycleStartDate`, or `usageCycleEndDate` is missing.' },
          { status: 400, when: 'A usage cycle date is not a valid `YYYY-MM-DD` date.' },
          { status: 403, when: 'Missing, invalid, or expired API key.' },
        ],
      },
    ],
  },

  'api-keys': {
    id: 'api-keys',
    label: 'API Keys',
    tagline: 'JWTs for server-to-server auth',
    description: 'API keys are signed JWTs that authenticate requests via the `X-Valyx-Signature` header. They can be issued with or without an expiry.',
    endpoints: [
      {
        id: 'generate-api-key',
        method: 'POST',
        path: '/app/apiKey/generate',
        title: 'Generate an API key',
        description: 'Issues a signed JWT for use in the `X-Valyx-Signature` header. Requires an **active session cookie** — an existing API key cannot be used to mint further keys.',
        authNote: 'session-only',
        requestBody: [
          { name: 'expiresInDays', type: 'integer', required: false, description: 'Positive integer. Omit entirely for a non-expiring key.' },
        ],
        requestExample: { expiresInDays: 30 },
        responseFields: [
          { name: 'apiKey', type: 'string', description: 'The signed JWT. Send it verbatim in the `X-Valyx-Signature` header.' },
          { name: 'expiresAt', type: 'string | null', description: 'Expiry date in `YYYY-MM-DD` format, or `null` for non-expiring keys.' },
        ],
        responseExamples: [
          {
            label: '201 Created',
            status: 201,
            body: envelope('API key generated successfully', {
              apiKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
              expiresAt: '2026-05-23',
            }),
          },
        ],
        extras: [
          {
            label: 'Usage',
            type: 'http',
            code:
`GET /app/tax/list HTTP/1.1
Host: api.valyx.com
X-Valyx-Signature: eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...`,
          },
        ],
        errors: [
          { status: 400, when: '`expiresInDays` is provided but is not a positive integer.' },
          { status: 403, when: 'No active session cookie, or the session token is expired.' },
        ],
      },
    ],
  },
};

// ── Enum types ───────────────────────────────────────────────────────────────

export const ENUMS = {
  TaxType: {
    id: 'TaxType',
    description: 'Values for the `type` field on tax rates and tax group constituents.',
    values: [
      { value: 'IGST', description: 'Integrated Goods and Services Tax' },
      { value: 'CGST', description: 'Central Goods and Services Tax' },
      { value: 'SGST', description: 'State Goods and Services Tax' },
      { value: 'UTGST', description: 'Union Territory Goods and Services Tax' },
      { value: 'CESS', description: 'Compensation Cess' },
    ],
  },
  Status: {
    id: 'Status',
    description: 'Generic active/inactive flag shared by most resources.',
    values: [
      { value: 'ACTIVE', description: 'In use. Returned by list endpoints.' },
      { value: 'INACTIVE', description: 'Archived. Excluded from list endpoints.' },
    ],
  },
  ContractStatus: {
    id: 'ContractStatus',
    description: 'Lifecycle states of a contract. New contracts must be created as `DRAFT`.',
    values: [
      { value: 'DRAFT', description: 'Being set up. The only status a contract can be created in.' },
      { value: 'SCHEDULED', description: 'Activated with a future `startDate` — it becomes `ACTIVE` when that date arrives.' },
      { value: 'ACTIVE', description: 'Live and billing. Requires at least one attached fee component.' },
      { value: 'CANCELLED', description: 'Cancelled via the cancel endpoint.' },
      { value: 'EXPIRED', description: 'Past its end date and not renewed.' },
      { value: 'INACTIVE', description: 'Archived.' },
    ],
  },
  TimeUnit: {
    id: 'TimeUnit',
    description: 'Unit of a `TimeInterval` — used for contract periods, billing frequencies, usage cycles, and estimate cycles.',
    values: [
      { value: 'DAY', description: 'Days.' },
      { value: 'WEEK', description: 'Weeks.' },
      { value: 'MONTH', description: 'Months.' },
      { value: 'YEAR', description: 'Years.' },
    ],
  },
  TaxCategory: {
    id: 'TaxCategory',
    description: 'Category of a fee component tax. Auto-derived from the tax `name` when omitted, falling back to `OTHER`.',
    values: [
      { value: 'GST', description: 'Goods and Services Tax.' },
      { value: 'CESS', description: 'Compensation cess.' },
      { value: 'SERVICE', description: 'Legacy service tax.' },
      { value: 'REVERSE_CHARGE', description: 'Reverse-charge tax, remitted by the buyer.' },
      { value: 'TDS', description: 'Tax deducted at source.' },
      { value: 'OTHER', description: 'Anything else. Unrecognized names fall back to this.' },
    ],
  },
  BillingDiscountType: {
    id: 'BillingDiscountType',
    description: 'Type of a billing discount.',
    values: [
      { value: 'ADJUSTMENT', description: 'An adjustment-style discount. Currently the only value.' },
    ],
  },
  BillingScheme: {
    id: 'BillingScheme',
    description: 'Values for the `billingScheme` field on a rate card. Determines which `description` fields are required.',
    values: [
      { value: 'PER_UNIT', description: 'A single rate for every unit. Requires `perUnitPrice`; tiers are rejected.' },
      { value: 'VOLUME', description: 'Tiered — the whole usage is priced at the rate of the tier it lands in. Requires `tiers`.' },
      { value: 'GRADUATED', description: 'Tiered — each portion of usage is priced at its own tier\'s rate. Requires `tiers`.' },
      { value: 'FIXED_RATE', description: 'A constant price regardless of usage. Requires `fixedPrice`; tiers are rejected.' },
      { value: 'MINMAX', description: 'Charges the minimum or maximum of a usage-based price and a flat rate. Requires `operationType`, `isPercentage`, and `perUsageValue`; tiers are rejected.' },
    ],
  },
  MinMaxOperation: {
    id: 'MinMaxOperation',
    description: 'Values for `description.operationType` on a `MINMAX` rate card.',
    values: [
      { value: 'MINIMUM', description: 'Charge the lower of the usage-based price and the flat rate.' },
      { value: 'MAXIMUM', description: 'Charge the higher of the usage-based price and the flat rate.' },
    ],
  },
  RateConfiguration: {
    id: 'RateConfiguration',
    description: 'Values for the `rateConfiguration` field on a product. Controls whether the product supports more than one price dimension.',
    values: [
      { value: 'SINGLE', description: 'One price dimension. No rate attributes can be attached to the product.' },
      { value: 'MULTIPLE', description: 'Multiple price dimensions. Rate attributes can be created against the product.' },
    ],
  },
  FeeType: {
    id: 'FeeType',
    description: 'Values for the `type` field on a fee. Determines how the fee is charged.',
    values: [
      { value: 'FIXED', description: 'A flat recurring charge — the amount does not depend on usage.' },
      { value: 'METERED', description: 'A usage-based charge. Billable quantity is computed from the fee\'s `usageAttributes`.' },
    ],
  },
  TaxIdentifierType: {
    id: 'TaxIdentifierType',
    description: 'Accepted values for the `type` field on a `TaxIdentifier`.',
    values: [
      { value: 'GSTIN', description: 'Indian GSTIN. The 15-character value is validated for format.' },
      { value: 'PAN', description: 'Indian PAN. Validated for format.' },
      { value: 'VAT', description: 'Generic VAT registration number for non-Indian jurisdictions.' },
      { value: 'OTHER', description: 'Any other identifier — format isn\'t validated.' },
    ],
  },
};

// ── Sidebar layout ───────────────────────────────────────────────────────────
//
// Controls the order and grouping of sub-pages in the API sidebar.
// `meta` items are rendered by name (see METAS below).
// `resource` items are rendered from RESOURCES.

export const SIDEBAR = [
  {
    label: 'Getting started',
    items: [
      { id: 'introduction', kind: 'meta' },
      { id: 'authentication', kind: 'meta' },
      { id: 'response-format', kind: 'meta' },
      { id: 'errors', kind: 'meta' },
    ],
  },
  {
    label: 'Core resources',
    items: [
      { id: 'tax-rates', kind: 'resource' },
      { id: 'tax-groups', kind: 'resource' },
      { id: 'customers', kind: 'resource' },
      { id: 'contacts', kind: 'resource' },
      { id: 'invoices', kind: 'resource' },
      { id: 'custom-fields', kind: 'resource' },
      { id: 'products', kind: 'resource' },
      { id: 'usage-attributes', kind: 'resource' },
      { id: 'rate-attributes', kind: 'resource' },
      { id: 'fees', kind: 'resource' },
      { id: 'fee-ratecards', kind: 'resource' },
      { id: 'contracts', kind: 'resource' },
      { id: 'contract-fee-components', kind: 'resource' },
    ],
  },
  {
    label: 'Authentication',
    items: [
      { id: 'api-keys', kind: 'resource' },
    ],
  },
  {
    label: 'Reference',
    items: [
      { id: 'tax-modelling', kind: 'meta' },
      { id: 'addresses', kind: 'meta' },
      { id: 'enums', kind: 'meta' },
    ],
  },
];

export const METAS = {
  introduction: { label: 'Introduction' },
  authentication: { label: 'Authentication' },
  'response-format': { label: 'Response format' },
  errors: { label: 'Errors' },
  'tax-modelling': { label: 'Tax modelling' },
  addresses: { label: 'Addresses' },
  enums: { label: 'Enum types' },
};
